/**
 * Campnect Alumni Network & Student Q&A Seed Dataset
 * 
 * Fictional demo dataset providing at least 26 distinct demo alumni accounts
 * and 25 thoroughly answered questions matching all prompt specifications.
 * All companies and affiliations are simulated demo entities.
 */

export interface DemoAlumniProfile {
  id: string
  user_id: string
  full_name: string
  username: string
  avatar_url: string
  company: string
  job_title: string
  industry: string
  graduation_year: number
  degree_course: string
  previous_college: string
  college_id: string
  bio: string
  skills: string[]
  expertise: string[]
  experience: {
    role: string
    company: string
    duration: string
    description: string
  }[]
  is_verified: number
  is_demo: number
  questions_answered_count?: number
  helpful_votes_count?: number
}

export interface DemoAnswerReply {
  id: string
  answer_id: string
  user_id: string
  author_name: string
  author_avatar?: string
  author_role: 'student' | 'alumni'
  content: string
  created_at: string
}

export interface DemoQAAnswer {
  id: string
  question_id: string
  author_id: string
  author_name: string
  author_username: string
  author_avatar: string
  author_title: string
  author_company: string
  is_verified_alumni: number
  content: string
  helpful_count: number
  created_at: string
  updated_at?: string
  replies?: DemoAnswerReply[]
}

export interface DemoQAQuestion {
  id: string
  user_id: string
  student_name: string
  student_username: string
  student_avatar: string
  student_college: string
  student_course?: string
  title: string
  description: string
  category: string
  tags: string[]
  college_context?: string
  course_context?: string
  status: 'open' | 'answered' | 'closed'
  answers_count: number
  tagged_alumni_ids: string[]
  created_at: string
  updated_at?: string
}

export const QA_CATEGORIES = [
  "Placements and Interviews",
  "Software Engineering and Technology",
  "Internships",
  "Career Guidance",
  "Resume and Portfolio",
  "Higher Education",
  "Competitive Exams",
  "Entrepreneurship and Startups",
  "Design and Product Management",
  "Finance and Business",
  "Communication and Professional Skills",
  "Campus Life and Career Preparation"
] as const

export type QACategory = typeof QA_CATEGORIES[number]

export const DEMO_ALUMNI: DemoAlumniProfile[] = [
  {
    "id": "alm_1",
    "user_id": "alm_1",
    "full_name": "Aarav Singhal",
    "username": "aarav.singhal.alumni",
    "avatar_url": "/demo/avatar-0.svg",
    "company": "Northstar Technologies",
    "job_title": "Senior Staff Software Engineer",
    "industry": "Software Engineering",
    "graduation_year": 2018,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "Delhi Technological University",
    "college_id": "c0",
    "bio": "Distributed systems engineer at Northstar Technologies (Simulated Demo Co.). Passionate about low-latency systems, Go, and mentoring college students through their first technical interview cycles.",
    "skills": [
      "Go",
      "Distributed Systems",
      "System Design",
      "PostgreSQL",
      "Microservices",
      "Kubernetes",
      "Java"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Placements and Interviews",
      "Internships",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Senior Staff Software Engineer",
        "company": "Northstar Technologies (Simulated)",
        "duration": "2022 - Present",
        "description": "Architecting cross-region event-streaming infrastructure processing 50M+ daily events with 99.99% uptime."
      },
      {
        "role": "Software Development Engineer II",
        "company": "Apex Cloud Systems (Simulated)",
        "duration": "2019 - 2022",
        "description": "Built distributed storage APIs and reduced database query p99 latencies by 42%."
      },
      {
        "role": "Associate Software Engineer",
        "company": "InnoTech Labs (Simulated)",
        "duration": "2018 - 2019",
        "description": "Developed backend REST microservices and automated deployment test suites."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 5,
    "helpful_votes_count": 42
  },
  {
    "id": "alm_2",
    "user_id": "alm_2",
    "full_name": "Priya Nair",
    "username": "priya.nair.alumni",
    "avatar_url": "/demo/avatar-1.svg",
    "company": "Starlight Studio Design",
    "job_title": "Lead UI/UX Product Designer",
    "industry": "UI/UX Design",
    "graduation_year": 2019,
    "degree_course": "B.Des in Interaction Design",
    "previous_college": "University of Delhi",
    "college_id": "c3",
    "bio": "Product designer focusing on accessible consumer interactions, design tokens, and design-to-code systems. Reviewing student portfolios and helping aspiring designers land design internships.",
    "skills": [
      "Figma",
      "Design Systems",
      "User Research",
      "Prototyping",
      "Accessibility (a11y)",
      "Wireframing",
      "Design Critique"
    ],
    "expertise": [
      "Design and Product Management",
      "Resume and Portfolio",
      "Internships"
    ],
    "experience": [
      {
        "role": "Lead Product Designer",
        "company": "Starlight Studio Design (Simulated)",
        "duration": "2022 - Present",
        "description": "Directing the cross-platform design system and onboarding workflows used by 4M+ active users."
      },
      {
        "role": "Senior UI/UX Designer",
        "company": "OmniBrand Digital (Simulated)",
        "duration": "2019 - 2022",
        "description": "Spearheaded user research and multi-step design validation tests across mobile web apps."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 28
  },
  {
    "id": "alm_3",
    "user_id": "alm_3",
    "full_name": "Rohan Deshmukh",
    "username": "rohan.deshmukh.alumni",
    "avatar_url": "/demo/avatar-2.svg",
    "company": "Cobalt Talent Partners",
    "job_title": "VP of Talent Acquisition & Campus Recruiting",
    "industry": "Human Resources and Recruitment",
    "graduation_year": 2016,
    "degree_course": "MBA in Human Resources",
    "previous_college": "University of Delhi",
    "college_id": "c3",
    "bio": "Overseeing early-career and campus hiring pipelines across tech, finance, and operations. Helping college freshers demystify resume parsing, ATS filters, and behavioral hiring rounds.",
    "skills": [
      "Technical Recruiting",
      "Resume Optimization",
      "Behavioral Interviews",
      "ATS Screening",
      "Salary Benchmarking",
      "Campus Drives"
    ],
    "expertise": [
      "Placements and Interviews",
      "Resume and Portfolio",
      "Internships",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "VP of Talent Acquisition",
        "company": "Cobalt Talent Partners (Simulated)",
        "duration": "2021 - Present",
        "description": "Managing end-to-end tech hiring partnerships across 30+ engineering campuses annually."
      },
      {
        "role": "Lead Campus Recruiter",
        "company": "Global Tech Hire (Simulated)",
        "duration": "2016 - 2021",
        "description": "Conducted 500+ fresher interviews and built structured entry-level evaluation rubrics."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 6,
    "helpful_votes_count": 55
  },
  {
    "id": "alm_4",
    "user_id": "alm_4",
    "full_name": "Ananya Krishnamurthy",
    "username": "ananya.k.alumni",
    "avatar_url": "/demo/avatar-3.svg",
    "company": "Zenith AI Labs",
    "job_title": "Senior Machine Learning Research Engineer",
    "industry": "Artificial Intelligence and Machine Learning",
    "graduation_year": 2020,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "IIT Delhi",
    "college_id": "c2",
    "bio": "Working on foundation models, fine-tuning techniques, and edge-deployable ML at Zenith AI Labs (Simulated Demo Co.). Advising undergraduate students on math prerequisites, research publications, and AI careers.",
    "skills": [
      "PyTorch",
      "Large Language Models",
      "NLP",
      "Computer Vision",
      "MLOps",
      "Vector Databases",
      "Python"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Higher Education",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Senior Machine Learning Engineer",
        "company": "Zenith AI Labs (Simulated)",
        "duration": "2022 - Present",
        "description": "Deploying optimized transformer models and RAG pipelines for domain-specific search."
      },
      {
        "role": "ML Engineer",
        "company": "Cortex Analytics (Simulated)",
        "duration": "2020 - 2022",
        "description": "Trained real-time recommendation models handling 100K queries per second."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 38
  },
  {
    "id": "alm_5",
    "user_id": "alm_5",
    "full_name": "Vikram Malhotra",
    "username": "vikram.m.alumni",
    "avatar_url": "/demo/avatar-4.svg",
    "company": "Apex Cloud Systems",
    "job_title": "Staff DevOps & Cloud Infrastructure Architect",
    "industry": "Cloud Computing and DevOps",
    "graduation_year": 2017,
    "degree_course": "B.Tech in Information Systems",
    "previous_college": "BITS Pilani",
    "college_id": "c5",
    "bio": "Cloud architect specializing in multi-region Kubernetes clusters, Terraform automation, and developer productivity tooling. Guiding students transitioning from local code to cloud-native production.",
    "skills": [
      "AWS",
      "Kubernetes",
      "Terraform",
      "CI/CD Pipelines",
      "Docker",
      "Prometheus & Grafana",
      "Linux"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Career Guidance",
      "Internships"
    ],
    "experience": [
      {
        "role": "Staff DevOps Architect",
        "company": "Apex Cloud Systems (Simulated)",
        "duration": "2021 - Present",
        "description": "Designed zero-downtime blue/green deployment systems for 120+ microservices on AWS EKS."
      },
      {
        "role": "Cloud Operations Engineer",
        "company": "ScaleStack Networks (Simulated)",
        "duration": "2017 - 2021",
        "description": "Automated infrastructure provisioning and lowered monthly cloud infrastructure costs by 35%."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 31
  },
  {
    "id": "alm_6",
    "user_id": "alm_6",
    "full_name": "Sneha Roy",
    "username": "sneha.roy.alumni",
    "avatar_url": "/demo/avatar-5.svg",
    "company": "Prism Consumer Apps",
    "job_title": "Lead Product Manager",
    "industry": "Product Management",
    "graduation_year": 2018,
    "degree_course": "B.Tech in Electronics & Communication",
    "previous_college": "Netaji Subhas University of Technology",
    "college_id": "c1",
    "bio": "Product manager bridging customer empathy, data telemetry, and engineering execution. Passionate about helping engineering and non-engineering students break into product management roles.",
    "skills": [
      "Product Strategy",
      "User Roadmaps",
      "A/B Testing",
      "PRD Writing",
      "SQL Analytics",
      "Stakeholder Management"
    ],
    "expertise": [
      "Design and Product Management",
      "Career Guidance",
      "Communication and Professional Skills"
    ],
    "experience": [
      {
        "role": "Lead Product Manager",
        "company": "Prism Consumer Apps (Simulated)",
        "duration": "2022 - Present",
        "description": "Spearheading viral social and gamification loops resulting in a 2.4x lift in 30-day retention."
      },
      {
        "role": "Associate Product Manager",
        "company": "FinTrack Labs (Simulated)",
        "duration": "2018 - 2022",
        "description": "Launched localized checkout flows and drove 18% improvement in payment conversion."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 26
  },
  {
    "id": "alm_7",
    "user_id": "alm_7",
    "full_name": "Kavita Sen",
    "username": "kavita.sen.alumni",
    "avatar_url": "/demo/avatar-6.svg",
    "company": "Beacon Cyber Labs",
    "job_title": "Senior Cybersecurity Analyst & Penetration Tester",
    "industry": "Cybersecurity",
    "graduation_year": 2021,
    "degree_course": "B.Tech in Information Security",
    "previous_college": "VIT Vellore",
    "college_id": "c4",
    "bio": "Defensive and offensive security practitioner. OSCP certified. Helping beginners navigate Capture The Flag (CTF) competitions, web application penetration testing, and ethical hacking fundamentals.",
    "skills": [
      "OWASP Top 10",
      "Penetration Testing",
      "Network Security",
      "Wireshark",
      "Burp Suite",
      "Linux Hardening",
      "Python Scripting"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Career Guidance",
      "Competitive Exams"
    ],
    "experience": [
      {
        "role": "Senior Cybersecurity Analyst",
        "company": "Beacon Cyber Labs (Simulated)",
        "duration": "2023 - Present",
        "description": "Conducting black-box and white-box security audits for enterprise banking clients."
      },
      {
        "role": "Security Engineer",
        "company": "Fortress Threat Defense (Simulated)",
        "duration": "2021 - 2023",
        "description": "Managed vulnerability disclosure programs and mitigated critical API authorization flaws."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 22
  },
  {
    "id": "alm_8",
    "user_id": "alm_8",
    "full_name": "Aditya Banerjee",
    "username": "aditya.b.alumni",
    "avatar_url": "/demo/avatar-7.svg",
    "company": "Velocity Mobility",
    "job_title": "Founder & CEO",
    "industry": "Entrepreneurship",
    "graduation_year": 2016,
    "degree_course": "B.Tech in Mechanical Engineering",
    "previous_college": "Delhi Technological University",
    "college_id": "c0",
    "bio": "Founded an on-demand campus micro-mobility startup out of a college hostel room. Raised seed funding and scaled to 6 campuses. Mentoring students navigating co-founder matching, customer discovery, and pitch decks.",
    "skills": [
      "Seed Fundraising",
      "Customer Discovery",
      "Lean Startup Methodology",
      "Pitch Decks",
      "Unit Economics",
      "Hiring"
    ],
    "expertise": [
      "Entrepreneurship and Startups",
      "Career Guidance",
      "Finance and Business"
    ],
    "experience": [
      {
        "role": "Founder & CEO",
        "company": "Velocity Mobility (Simulated)",
        "duration": "2019 - Present",
        "description": "Scaled EV fleet operations across 6 tertiary institutions serving 40,000+ monthly rides."
      },
      {
        "role": "Operations Lead",
        "company": "Urban Transit Co. (Simulated)",
        "duration": "2016 - 2019",
        "description": "Led launch teams in 3 metropolitan areas optimizing vehicle turnaround times."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 34
  },
  {
    "id": "alm_9",
    "user_id": "alm_9",
    "full_name": "Neha Gupta",
    "username": "neha.gupta.alumni",
    "avatar_url": "/demo/avatar-0.svg",
    "company": "Vanguard Capital Partners",
    "job_title": "Quantitative Risk & Financial Analyst",
    "industry": "Finance",
    "graduation_year": 2019,
    "degree_course": "B.Com (Hons) + FRM Certified",
    "previous_college": "University of Delhi",
    "college_id": "c3",
    "bio": "Equity research and risk management analyst at Vanguard Capital Partners (Simulated Demo Co.). Assisting students preparing for finance internships, CFA/FRM accreditations, and investment banking recruitment.",
    "skills": [
      "Financial Modeling",
      "Python for Finance",
      "Valuation (DCF & Comps)",
      "Bloomberg Terminal",
      "Statistical Arbitrage",
      "Excel"
    ],
    "expertise": [
      "Finance and Business",
      "Placements and Interviews",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Quantitative Risk Analyst",
        "company": "Vanguard Capital Partners (Simulated)",
        "duration": "2021 - Present",
        "description": "Automated portfolio stress-testing algorithms evaluating $200M+ in fixed-income exposures."
      },
      {
        "role": "Financial Analyst",
        "company": "Apex Equities (Simulated)",
        "duration": "2019 - 2021",
        "description": "Authored quarterly valuation reports on consumer tech equities for institutional clients."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 27
  },
  {
    "id": "alm_10",
    "user_id": "alm_10",
    "full_name": "Karthik Sundaram",
    "username": "karthik.s.alumni",
    "avatar_url": "/demo/avatar-1.svg",
    "company": "Nexus Digital Systems",
    "job_title": "Principal Full-Stack Architect",
    "industry": "Full-Stack Development",
    "graduation_year": 2017,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "IIT Delhi",
    "college_id": "c2",
    "bio": "Full-stack software architect with 8+ years building high-traffic web applications with TypeScript, React, and Node.js. Helping students choose tech stacks, structure clean code, and prepare for system design rounds.",
    "skills": [
      "React",
      "Node.js",
      "TypeScript",
      "GraphQL",
      "PostgreSQL",
      "Redis",
      "Full-Stack Architecture"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Placements and Interviews",
      "Internships"
    ],
    "experience": [
      {
        "role": "Principal Full-Stack Architect",
        "company": "Nexus Digital Systems (Simulated)",
        "duration": "2022 - Present",
        "description": "Standardized TypeScript monorepo architecture and SSR hydration for 10M+ web visitors."
      },
      {
        "role": "Senior Full-Stack Engineer",
        "company": "CloudPulse (Simulated)",
        "duration": "2017 - 2022",
        "description": "Built real-time collaborative workspace tools with WebSockets and optimistic caching."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 5,
    "helpful_votes_count": 46
  },
  {
    "id": "alm_11",
    "user_id": "alm_11",
    "full_name": "Tanvi Saxena",
    "username": "tanvi.saxena.alumni",
    "avatar_url": "/demo/avatar-2.svg",
    "company": "Hyperion Global Advisory",
    "job_title": "Senior Business & Data Analytics Consultant",
    "industry": "Data Analytics",
    "graduation_year": 2020,
    "degree_course": "B.Tech in Information Technology",
    "previous_college": "Netaji Subhas University of Technology",
    "college_id": "c1",
    "bio": "Translating massive enterprise data warehouses into clear strategic decisions. Helping students learn pragmatic SQL, dashboard design, business acumen, and commercial storytelling.",
    "skills": [
      "Advanced SQL",
      "Python (Pandas/NumPy)",
      "Tableau",
      "PowerBI",
      "Data Warehousing",
      "A/B Testing Analysis"
    ],
    "expertise": [
      "Finance and Business",
      "Software Engineering and Technology",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Senior Analytics Consultant",
        "company": "Hyperion Global Advisory (Simulated)",
        "duration": "2022 - Present",
        "description": "Built executive BI telemetry suites tracking $120M in supply-chain operational spending."
      },
      {
        "role": "Data Analyst",
        "company": "MetricsFlow Labs (Simulated)",
        "duration": "2020 - 2022",
        "description": "Formulated predictive churn models delivering an 8% boost in enterprise contract renewals."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 30
  },
  {
    "id": "alm_12",
    "user_id": "alm_12",
    "full_name": "Arjun Mathur",
    "username": "arjun.mathur.alumni",
    "avatar_url": "/demo/avatar-3.svg",
    "company": "Pinnacle Strategy Group",
    "job_title": "Management Consultant",
    "industry": "Consulting",
    "graduation_year": 2018,
    "degree_course": "B.Tech in Chemical + MBA",
    "previous_college": "BITS Pilani",
    "college_id": "c5",
    "bio": "Advising Fortune 500 leadership on digital transformation, operational efficiency, and market expansion. Coaching students through case interview frameworks, guesstimates, and structured communication.",
    "skills": [
      "Case Interview Prep",
      "Market Sizing & Guesstimates",
      "Executive Communication",
      "Business Model Strategy",
      "Problem Structuring"
    ],
    "expertise": [
      "Career Guidance",
      "Placements and Interviews",
      "Communication and Professional Skills"
    ],
    "experience": [
      {
        "role": "Management Consultant",
        "company": "Pinnacle Strategy Group (Simulated)",
        "duration": "2021 - Present",
        "description": "Led cross-border market entry analyses for clean-energy industrial clients across EMEA."
      },
      {
        "role": "Associate Consultant",
        "company": "Strategic Edge Partners (Simulated)",
        "duration": "2018 - 2021",
        "description": "Developed supply optimization models reducing manufacturing downtime by 14%."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 25
  },
  {
    "id": "alm_13",
    "user_id": "alm_13",
    "full_name": "Divya Chawla",
    "username": "divya.chawla.alumni",
    "avatar_url": "/demo/avatar-4.svg",
    "company": "Lumina Web Platforms",
    "job_title": "Staff Frontend Platform Engineer",
    "industry": "Software Engineering",
    "graduation_year": 2020,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "Delhi Technological University",
    "college_id": "c0",
    "bio": "Obsessed with browser performance, sub-second TTFB, micro-frontends, and accessible interactive interfaces. Mentoring frontend beginners on React internals, JavaScript closures, and CSS architecture.",
    "skills": [
      "React",
      "Next.js",
      "Core Web Vitals",
      "Tailwind CSS",
      "Browser Rendering Engine",
      "TypeScript",
      "Web Accessibility"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Internships",
      "Resume and Portfolio"
    ],
    "experience": [
      {
        "role": "Staff Frontend Engineer",
        "company": "Lumina Web Platforms (Simulated)",
        "duration": "2023 - Present",
        "description": "Achieved 99+ Google Lighthouse performance scores across 40 consumer-facing pages."
      },
      {
        "role": "Frontend Engineer",
        "company": "WebSpark Media (Simulated)",
        "duration": "2020 - 2023",
        "description": "Built responsive design component libraries with zero-dependency TypeScript modules."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 29
  },
  {
    "id": "alm_14",
    "user_id": "alm_14",
    "full_name": "Manish Bhatia",
    "username": "manish.bhatia.alumni",
    "avatar_url": "/demo/avatar-5.svg",
    "company": "Streamline Data Systems",
    "job_title": "Senior Backend & Data Platform Engineer",
    "industry": "Software Engineering",
    "graduation_year": 2019,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "VIT Vellore",
    "college_id": "c4",
    "bio": "Distributed caching, database sharding, asynchronous message queues, and bulletproof transactional backends. Guiding students in low-level design (LLD) and scalable API architecture.",
    "skills": [
      "Java Spring Boot",
      "Apache Kafka",
      "PostgreSQL",
      "Redis Cluster",
      "Concurrency & Multithreading",
      "Low-Level Design (LLD)"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Placements and Interviews"
    ],
    "experience": [
      {
        "role": "Senior Backend Engineer",
        "company": "Streamline Data Systems (Simulated)",
        "duration": "2022 - Present",
        "description": "Engineered high-throughput payment reconciliation queues processing $4M+ daily."
      },
      {
        "role": "Backend Developer",
        "company": "CoreServe Technologies (Simulated)",
        "duration": "2019 - 2022",
        "description": "Designed relational schemas and normalized query execution plans reducing latency."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 24
  },
  {
    "id": "alm_15",
    "user_id": "alm_15",
    "full_name": "Isha Mehra",
    "username": "isha.mehra.alumni",
    "avatar_url": "/demo/avatar-6.svg",
    "company": "Global Academic Research Institute",
    "job_title": "Doctoral Scholar & Higher Education Mentor",
    "industry": "Higher Education and Research",
    "graduation_year": 2020,
    "degree_course": "M.Sc + Ph.D. Candidate in Computer Science",
    "previous_college": "IIT Delhi",
    "college_id": "c2",
    "bio": "Researcher published in top-tier ACM and IEEE conferences. Helping undergraduates evaluate MS vs Ph.D., craft strong Statements of Purpose (SOPs), approach professors for research, and target international scholarships.",
    "skills": [
      "Academic Writing",
      "Statement of Purpose (SOP)",
      "Research Methodology",
      "GRE & TOEFL Strategy",
      "Faculty Cold Emails",
      "Conference Publications"
    ],
    "expertise": [
      "Higher Education",
      "Career Guidance",
      "Competitive Exams"
    ],
    "experience": [
      {
        "role": "Doctoral Researcher",
        "company": "Global Academic Research Institute (Simulated)",
        "duration": "2021 - Present",
        "description": "Investigating privacy-preserving federated machine learning under NSF-funded fellowships."
      },
      {
        "role": "Research Assistant",
        "company": "IIT Delhi AI Lab (Simulated)",
        "duration": "2019 - 2021",
        "description": "Published 3 peer-reviewed papers on algorithmic bias and fairness metrics."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 36
  },
  {
    "id": "alm_16",
    "user_id": "alm_16",
    "full_name": "Siddharth Rawat",
    "username": "siddharth.r.alumni",
    "avatar_url": "/demo/avatar-7.svg",
    "company": "Elevate Brand Labs",
    "job_title": "Growth Marketing Director",
    "industry": "Marketing",
    "graduation_year": 2017,
    "degree_course": "B.A. in Economics",
    "previous_college": "University of Delhi",
    "college_id": "c3",
    "bio": "Data-driven growth marketer managing multi-million dollar performance budgets. Coaching students on organic acquisition, digital marketing analytics, and bridging creativity with quantitative results.",
    "skills": [
      "Growth Marketing",
      "User Acquisition",
      "Conversion Rate Optimization (CRO)",
      "Content Strategy",
      "Google Analytics 4",
      "Email Funnels"
    ],
    "expertise": [
      "Finance and Business",
      "Communication and Professional Skills",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Director of Growth Marketing",
        "company": "Elevate Brand Labs (Simulated)",
        "duration": "2021 - Present",
        "description": "Scaled brand app acquisition from 50K to 800K monthly active downloads at $1.10 CAC."
      },
      {
        "role": "Digital Marketing Specialist",
        "company": "NextGen Media (Simulated)",
        "duration": "2017 - 2021",
        "description": "Optimized search ad copy and multi-channel attribution funnels."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 2,
    "helpful_votes_count": 18
  },
  {
    "id": "alm_17",
    "user_id": "alm_17",
    "full_name": "Pooja Hegde",
    "username": "pooja.hegde.alumni",
    "avatar_url": "/demo/avatar-0.svg",
    "company": "Catalyst Campus Works",
    "job_title": "Early Career Program Manager & Tech Mentor",
    "industry": "Human Resources and Recruitment",
    "graduation_year": 2021,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "Netaji Subhas University of Technology",
    "college_id": "c1",
    "bio": "Passionate about leveling the playing field for tier-2 and tier-3 college students. Specialized in cold outreach scripts on LinkedIn, identifying legitimate off-campus internships, and first 90-day workplace etiquette.",
    "skills": [
      "Fresher Mentorship",
      "Cold Outreach Strategy",
      "GitHub Portfolio Review",
      "Workplace Etiquette",
      "Mock Interviews",
      "Placement Mindset"
    ],
    "expertise": [
      "Internships",
      "Resume and Portfolio",
      "Campus Life and Career Preparation",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Early Career Program Lead",
        "company": "Catalyst Campus Works (Simulated)",
        "duration": "2023 - Present",
        "description": "Designing summer internship bootcamps and structured mentorship programs for 600+ undergraduates."
      },
      {
        "role": "Talent Associate",
        "company": "SkillBridge Tech (Simulated)",
        "duration": "2021 - 2023",
        "description": "Matched 200+ engineering freshers with verified product-based internship openings."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 5,
    "helpful_votes_count": 49
  },
  {
    "id": "alm_18",
    "user_id": "alm_18",
    "full_name": "Gaurav Taneja",
    "username": "gaurav.t.alumni",
    "avatar_url": "/demo/avatar-1.svg",
    "company": "Horizon Cloud Matrix",
    "job_title": "Enterprise Solutions Architect",
    "industry": "Cloud Computing and DevOps",
    "graduation_year": 2016,
    "degree_course": "B.Tech in Electrical Engineering",
    "previous_college": "BITS Pilani",
    "college_id": "c5",
    "bio": "10 years navigating complex multi-cloud migrations, hybrid enterprise networks, and cloud cost governance. Helping engineering students build their cloud credentials, AWS/Azure certifications, and system breadth.",
    "skills": [
      "Cloud Architecture",
      "AWS Certified Solutions Architect",
      "Zero Trust Networks",
      "Disaster Recovery",
      "Hybrid Cloud",
      "SLA Engineering"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Enterprise Solutions Architect",
        "company": "Horizon Cloud Matrix (Simulated)",
        "duration": "2020 - Present",
        "description": "Architecting mission-critical cloud infrastructure for Fortune 100 logistics and retail clients."
      },
      {
        "role": "Senior Systems Engineer",
        "company": "InfraScale Global (Simulated)",
        "duration": "2016 - 2020",
        "description": "Migrated monolithic legacy on-prem datacenters to containerized AWS workloads."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 2,
    "helpful_votes_count": 19
  },
  {
    "id": "alm_19",
    "user_id": "alm_19",
    "full_name": "Ritu Parashar",
    "username": "ritu.parashar.alumni",
    "avatar_url": "/demo/avatar-2.svg",
    "company": "OpenMesh Technologies",
    "job_title": "Lead Developer Relations & Technical Writer",
    "industry": "Communication and Professional Skills",
    "graduation_year": 2020,
    "degree_course": "B.Tech in Information Technology",
    "previous_college": "Delhi Technological University",
    "college_id": "c0",
    "bio": "Author of widely read developer documentation, technical tutorials, and open-source guides. Helping engineers improve technical writing, articulate complex ideas simply, and speak with confidence in interviews.",
    "skills": [
      "Technical Documentation",
      "Developer Advocacy",
      "Open Source Community",
      "Public Speaking",
      "API Guides",
      "Storytelling"
    ],
    "expertise": [
      "Communication and Professional Skills",
      "Software Engineering and Technology",
      "Resume and Portfolio"
    ],
    "experience": [
      {
        "role": "Lead Developer Relations Engineer",
        "company": "OpenMesh Technologies (Simulated)",
        "duration": "2022 - Present",
        "description": "Grew developer community from 3K to 45K members and authored reference API architecture guides."
      },
      {
        "role": "Technical Writer",
        "company": "DevDoc Systems (Simulated)",
        "duration": "2020 - 2022",
        "description": "Standardized SDK reference manuals and developer onboarding walkthroughs."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 26
  },
  {
    "id": "alm_20",
    "user_id": "alm_20",
    "full_name": "Varun Khanna",
    "username": "varun.khanna.alumni",
    "avatar_url": "/demo/avatar-3.svg",
    "company": "SwiftLogistics Corp",
    "job_title": "Product Operations & Analytics Lead",
    "industry": "Business Analytics",
    "graduation_year": 2019,
    "degree_course": "B.Tech in Mechanical + PMP",
    "previous_college": "VIT Vellore",
    "college_id": "c4",
    "bio": "Specialist in scaling operational tech products, real-time telemetry, routing engines, and delivery analytics. Teaching students how to bridge code with field reality and business operations.",
    "skills": [
      "Product Operations",
      "Supply Chain Analytics",
      "Agile/Scrum",
      "Incident Root Cause Analysis",
      "Process Optimization"
    ],
    "expertise": [
      "Design and Product Management",
      "Finance and Business",
      "Career Guidance"
    ],
    "experience": [
      {
        "role": "Product Operations Lead",
        "company": "SwiftLogistics Corp (Simulated)",
        "duration": "2022 - Present",
        "description": "Optimized last-mile dispatch routing algorithms cutting per-delivery fuel costs by 11%."
      },
      {
        "role": "Operations Analyst",
        "company": "CargoWave Express (Simulated)",
        "duration": "2019 - 2022",
        "description": "Automated warehouse throughput tracking and created cross-functional exception dashboards."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 2,
    "helpful_votes_count": 17
  },
  {
    "id": "alm_21",
    "user_id": "alm_21",
    "full_name": "Shweta Kulkarni",
    "username": "shweta.k.alumni",
    "avatar_url": "/demo/avatar-4.svg",
    "company": "NeuralCraft Solutions",
    "job_title": "Senior NLP & LLM Systems Engineer",
    "industry": "Artificial Intelligence and Machine Learning",
    "graduation_year": 2021,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "IIT Delhi",
    "college_id": "c2",
    "bio": "Building enterprise LLM fine-tuning pipelines, evaluation harnesses, and low-latency inference endpoints. Guiding students through the transition from Kaggle toy notebooks to production-grade AI systems.",
    "skills": [
      "LangChain / LlamaIndex",
      "HuggingFace",
      "Transformers",
      "Vector Embeddings",
      "Model Distillation",
      "Python",
      "Evaluation Benchmarks"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Higher Education",
      "Internships"
    ],
    "experience": [
      {
        "role": "Senior LLM Systems Engineer",
        "company": "NeuralCraft Solutions (Simulated)",
        "duration": "2023 - Present",
        "description": "Building custom RAG architectures for legal and medical search with sub-500ms latency."
      },
      {
        "role": "Associate NLP Engineer",
        "company": "CognitiveWave (Simulated)",
        "duration": "2021 - 2023",
        "description": "Fine-tuned multilingual BERT variants for sentiment classification across 8 Indian languages."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 31
  },
  {
    "id": "alm_22",
    "user_id": "alm_22",
    "full_name": "Abhishek Pandey",
    "username": "abhishek.p.alumni",
    "avatar_url": "/demo/avatar-5.svg",
    "company": "CodePulse Academy",
    "job_title": "Algorithms Specialist & CP Mentor",
    "industry": "Software Engineering",
    "graduation_year": 2019,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "Delhi Technological University",
    "college_id": "c0",
    "bio": "Candidate Master on Codeforces, 6x ICPC Regionalist, now coaching students on dynamic programming, graph algorithms, and time complexity tradeoffs for Tier-1 product placement interviews.",
    "skills": [
      "Data Structures & Algorithms",
      "Competitive Programming",
      "Dynamic Programming",
      "Graph Theory",
      "Time & Space Optimization",
      "C++ STL"
    ],
    "expertise": [
      "Placements and Interviews",
      "Competitive Exams",
      "Software Engineering and Technology"
    ],
    "experience": [
      {
        "role": "Lead Algorithms Specialist",
        "company": "CodePulse Academy (Simulated)",
        "duration": "2021 - Present",
        "description": "Authored 150+ original algorithmic challenges and coached 2,000+ students for coding rounds."
      },
      {
        "role": "Software Engineer",
        "company": "FastTrack Systems (Simulated)",
        "duration": "2019 - 2021",
        "description": "Optimized internal algorithmic search engines and indexed data processing pipelines."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 41
  },
  {
    "id": "alm_23",
    "user_id": "alm_23",
    "full_name": "Meenal Agarwal",
    "username": "meenal.a.alumni",
    "avatar_url": "/demo/avatar-6.svg",
    "company": "Novus Scale Technologies",
    "job_title": "People & Culture Business Partner",
    "industry": "Human Resources and Recruitment",
    "graduation_year": 2018,
    "degree_course": "B.A. in Psychology + MBA in HR",
    "previous_college": "University of Delhi",
    "college_id": "c3",
    "bio": "HR business partner focused on performance calibration, employee mental health, and inclusive hiring practices. Demystifying rejection handling, resilience during campus placement season, and negotiation.",
    "skills": [
      "Placement Stress Management",
      "Behavioral Calibration",
      "STAR Technique Coaching",
      "Workplace Psychology",
      "Conflict Resolution"
    ],
    "expertise": [
      "Communication and Professional Skills",
      "Campus Life and Career Preparation",
      "Placements and Interviews"
    ],
    "experience": [
      {
        "role": "People Partner",
        "company": "Novus Scale Technologies (Simulated)",
        "duration": "2021 - Present",
        "description": "Partnering with engineering VPs on team retention, career level progression, and wellness initiatives."
      },
      {
        "role": "HR Specialist",
        "company": "PeakTalent Human Resources (Simulated)",
        "duration": "2018 - 2021",
        "description": "Coordinated campus hiring and fresher onboarding across 15 engineering disciplines."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 4,
    "helpful_votes_count": 37
  },
  {
    "id": "alm_24",
    "user_id": "alm_24",
    "full_name": "Deepak Joshi",
    "username": "deepak.joshi.alumni",
    "avatar_url": "/demo/avatar-7.svg",
    "company": "AppSphere Mobility",
    "job_title": "Mobile Platform Lead",
    "industry": "Full-Stack Development",
    "graduation_year": 2018,
    "degree_course": "B.Tech in Computer Science",
    "previous_college": "Netaji Subhas University of Technology",
    "college_id": "c1",
    "bio": "Architecting offline-first mobile apps for Android and iOS using React Native and Kotlin. Helping college students turn side projects into production apps published on Google Play and the App Store.",
    "skills": [
      "React Native",
      "Kotlin",
      "Android SDK",
      "Offline-First Architecture",
      "Capacitor",
      "Mobile CI/CD",
      "App Store Guidelines"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Internships",
      "Resume and Portfolio"
    ],
    "experience": [
      {
        "role": "Mobile Engineering Lead",
        "company": "AppSphere Mobility (Simulated)",
        "duration": "2021 - Present",
        "description": "Architected unified mobile app codebase maintaining 4.8-star rating across 2M downloads."
      },
      {
        "role": "Android Engineer",
        "company": "PocketApp Studio (Simulated)",
        "duration": "2018 - 2021",
        "description": "Built high-performance native Android fragments and integrated Bluetooth BLE peripherals."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 23
  },
  {
    "id": "alm_25",
    "user_id": "alm_25",
    "full_name": "Aakash Bhardwaj",
    "username": "aakash.b.alumni",
    "avatar_url": "/demo/avatar-0.svg",
    "company": "Frontier Seed Capital",
    "job_title": "Startup Operator & Venture Associate",
    "industry": "Entrepreneurship",
    "graduation_year": 2020,
    "degree_course": "B.Tech in Manufacturing + Minor in Finance",
    "previous_college": "BITS Pilani",
    "college_id": "c5",
    "bio": "Analyzing early-stage technology startups, SaaS metrics, and venture capital financing terms. Advising students evaluating early-stage startup job offers versus MNC placements, equity options, and ESOP vesting.",
    "skills": [
      "Startup Offer Evaluation",
      "ESOP & Equity Basics",
      "Venture Capital Diligence",
      "Cap Tables",
      "Market Sizing",
      "Tech Startup Ecosystem"
    ],
    "expertise": [
      "Entrepreneurship and Startups",
      "Career Guidance",
      "Finance and Business"
    ],
    "experience": [
      {
        "role": "Venture Associate",
        "company": "Frontier Seed Capital (Simulated)",
        "duration": "2022 - Present",
        "description": "Sourcing and conducting investment diligence on pre-seed and seed stage B2B software companies."
      },
      {
        "role": "Founder Associate",
        "company": "Stealth SaaS Startup (Simulated)",
        "duration": "2020 - 2022",
        "description": "Worked alongside founders on early sales pipelines, investor updates, and financial modeling."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 3,
    "helpful_votes_count": 29
  },
  {
    "id": "alm_26",
    "user_id": "alm_26",
    "full_name": "Harshita Rao",
    "username": "harshita.rao.alumni",
    "avatar_url": "/demo/avatar-1.svg",
    "company": "CloudGate Infrastructure",
    "job_title": "Site Reliability Engineer (SRE)",
    "industry": "Cloud Computing and DevOps",
    "graduation_year": 2022,
    "degree_course": "B.Tech in Information Technology",
    "previous_college": "VIT Vellore",
    "college_id": "c4",
    "bio": "Recent graduate working in mission-critical reliability engineering. Active mentor for early-career students on how to land and thrive in SRE, Linux administration, and on-call rotations right after college.",
    "skills": [
      "Linux System Internals",
      "Incident Management",
      "Bash & Python Scripting",
      "Prometheus",
      "SLOs & Error Budgets",
      "Post-Mortems"
    ],
    "expertise": [
      "Software Engineering and Technology",
      "Internships",
      "Campus Life and Career Preparation"
    ],
    "experience": [
      {
        "role": "Site Reliability Engineer",
        "company": "CloudGate Infrastructure (Simulated)",
        "duration": "2022 - Present",
        "description": "Maintaining 99.95% uptime SLOs for global distributed DNS and ingress edge routers."
      },
      {
        "role": "Cloud Engineering Intern",
        "company": "Apex Cloud Systems (Simulated)",
        "duration": "2021 - 2022",
        "description": "Automated monitoring alerts and reduced pager noise by 30% through log metric filters."
      }
    ],
    "is_verified": 1,
    "is_demo": 1,
    "questions_answered_count": 2,
    "helpful_votes_count": 20
  }
]

export interface SeededQuestionWithAnswers {
  question: DemoQAQuestion
  answers: DemoQAAnswer[]
}

export const SEEDED_QA_DATA: SeededQuestionWithAnswers[] = [
  {
    "question": {
      "id": "q1",
      "user_id": "u1",
      "student_name": "Aanya Sharma",
      "student_username": "aanya.sharma",
      "student_avatar": "/demo/avatar-0.svg",
      "student_college": "Delhi Technological University",
      "student_course": "B.Tech Computer Science",
      "title": "How should a first-year student start learning programming?",
      "description": "I just completed my first semester in computer science. I feel overwhelmed by the sheer number of languages and frameworks being discussed by seniors (C++, Python, Java, JavaScript, Rust). What is a realistic, step-by-step roadmap to build strong programming fundamentals without burning out?",
      "category": "Software Engineering and Technology",
      "tags": [
        "FirstYear",
        "ProgrammingBasics",
        "Roadmap",
        "CSFundamentals"
      ],
      "college_context": "DTU",
      "course_context": "B.Tech 1st Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_1",
        "alm_10"
      ],
      "created_at": "2026-10-03T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_1_1",
        "question_id": "q1",
        "author_id": "alm_1",
        "author_name": "Aarav Singhal",
        "author_username": "aarav.singhal.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Senior Staff Software Engineer",
        "author_company": "Northstar Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 24,
        "content": "First of all, take a deep breath. What you are feeling is completely normal—almost every engineering student experiences \"choice paralysis\" in their first year.\n\nHere is a practical, 3-phase roadmap that worked wonders for me and dozens of juniors I have mentored:\n\n### 1. Pick ONE Language and Stay With It for at Least 6 Months\nDo not jump between C++, Java, and Python every two weeks. \n- If your university curriculum starts with **C or C++**, embrace it! C/C++ forces you to understand memory addresses, pointers, and how hardware actually handles your data.\n- If you want immediate visual results and script automation, **Python** is equally fine.\nThe key is: **Learn computational thinking, not language syntax.** Variables, control flow, functions, recursion, and object-oriented paradigms transfer 95% from one language to another.\n\n### 2. Solve 50-70 Fundamental Problems Before Opening LeetCode\nDo not jump straight into competitive programming. Go to platforms like HackerRank or standard CS textbooks. Write programs that:\n- Manipulate arrays (reversing, finding second-largest, duplicate detection).\n- Parse and format strings.\n- Implement basic sorting algorithms (Bubble, Insertion, Merge Sort) by hand.\nUnderstanding *why* Merge Sort is faster than Bubble Sort gives you intuitive algorithmic appreciation.\n\n### 3. Build a Small CLI Project That You Actually Use\nAfter 2-3 months, build a terminal-based expense tracker, a simple quiz game, or a college library manager. When you have to store data in a text file and retrieve it, programming transforms from theoretical syntax into an empowering superpower.\n\nFocus on depth over breadth in your first year. The frameworks will come naturally once your foundation is solid.",
        "created_at": "2026-10-04T14:03:00.312Z",
        "replies": [
          {
            "id": "rep_1_1",
            "answer_id": "ans_1_1",
            "user_id": "u1",
            "author_name": "Aanya Sharma",
            "author_role": "student",
            "content": "Thank you so much Aarav! Our college starts with C++ next semester, so I will stick with C++ and master the basics first rather than stressing over web frameworks right now.",
            "created_at": "2026-10-05T14:03:00.312Z"
          },
          {
            "id": "rep_1_2",
            "answer_id": "ans_1_1",
            "user_id": "alm_1",
            "author_name": "Aarav Singhal",
            "author_role": "alumni",
            "content": "Spot-on decision Aanya. C++ is an exceptional choice—it gives you high performance and deep understanding of system mechanics. Keep building!",
            "created_at": "2026-10-06T14:03:00.312Z"
          }
        ]
      },
      {
        "id": "ans_1_2",
        "question_id": "q1",
        "author_id": "alm_10",
        "author_name": "Karthik Sundaram",
        "author_username": "karthik.s.alumni",
        "author_avatar": "/demo/avatar-1.svg",
        "author_title": "Principal Full-Stack Architect",
        "author_company": "Nexus Digital Systems (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 14,
        "content": "Adding to Aarav's excellent advice: learn to use **Git and GitHub** during your first year. \n\nCommit every small script you write, even if it is just a 20-line palindrome checker. Getting comfortable with `git add`, `git commit`, and `git push` early removes the intimidation factor when you join hackathons or team projects in your second year.",
        "created_at": "2026-10-05T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q2",
      "user_id": "u2",
      "student_name": "Kabir Mehta",
      "student_username": "kabir.mehta",
      "student_avatar": "/demo/avatar-1.svg",
      "student_college": "Netaji Subhas University of Technology",
      "student_course": "B.Tech Information Technology",
      "title": "Which projects are useful for a software engineering internship?",
      "description": "Every fresher seems to build a To-Do List app, a Weather app, or a clone of Netflix/Spotify following a YouTube tutorial. Recruiters probably see thousands of these. What kind of projects actually stand out on an internship resume?",
      "category": "Internships",
      "tags": [
        "Projects",
        "Internships",
        "ResumeBuilder",
        "WebDev"
      ],
      "college_context": "NSUT",
      "course_context": "B.Tech 2nd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_1",
        "alm_3",
        "alm_17"
      ],
      "created_at": "2026-10-05T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_2_1",
        "question_id": "q2",
        "author_id": "alm_1",
        "author_name": "Aarav Singhal",
        "author_username": "aarav.singhal.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Senior Staff Software Engineer",
        "author_company": "Northstar Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 31,
        "content": "You hit the nail on the head. As someone who interviews interns, when I see \"Netflix Clone\" on a resume, my eyes glaze over because 99% of candidates just followed a 6-hour YouTube tutorial without understanding the architecture.\n\nHere are 3 project concepts that immediately stand out because they demonstrate **engineering tradeoffs and system depth**:\n\n### 1. A Real-Time Collaborative Tool (Using WebSockets)\nBuild a collaborative whiteboard, live poll system, or markdown document editor.\n- **Why it impresses:** It forces you to handle concurrency, reconnection states, optimistic UI updates, and conflict resolution (like Last-Write-Wins or operational transforms).\n- **Bonus:** Add Redis Pub/Sub so it can scale to multiple server nodes.\n\n### 2. A Distributed Background Task Worker with Dead-Letter Queues\nBuild a service that takes long-running tasks (e.g. video transcode simulation, email sending, PDF generator), puts them into a persistent queue (RabbitMQ, Redis, or BullMQ), processes them with worker pools, and handles failures with exponential backoff retries.\n- **Why it impresses:** Most junior devs only know synchronous request-response. Asynchronous queues show you understand real production challenges.\n\n### 3. A Database or Cache Engine Simulation\nWrite a simple in-memory key-value store with TTL (time to live), LRU eviction policy, and disk-persistence append-only logs.\n- **Why it impresses:** It proves you understand data structures (HashMaps + Doubly Linked Lists) and disk I/O beyond just calling `npm install`.\n\n**Crucial tip:** In your GitHub README, include an architecture diagram, test coverage instructions, and a paragraph explaining: *\"If 100,000 users accessed this concurrently, what would break first and how would I fix it?\"* That paragraph alone gets you interview callbacks.",
        "created_at": "2026-10-06T14:03:00.312Z",
        "replies": [
          {
            "id": "rep_2_1",
            "answer_id": "ans_2_1",
            "user_id": "u2",
            "author_name": "Kabir Mehta",
            "author_role": "student",
            "content": "The distributed task queue idea sounds phenomenal. I have already learned Redis basics, so building a worker pool with retry queues will be a huge step up!",
            "created_at": "2026-10-07T14:03:00.312Z"
          }
        ]
      },
      {
        "id": "ans_2_2",
        "question_id": "q2",
        "author_id": "alm_17",
        "author_name": "Pooja Hegde",
        "author_username": "pooja.hegde.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Early Career Program Manager & Tech Mentor",
        "author_company": "Catalyst Campus Works (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 19,
        "content": "From a recruiter and screening perspective, here is what gets your project noticed in 15 seconds:\n\n1. **A live deployed URL that works instantly:** Deploy on Vercel, Render, or Railway. If there is authentication, provide a \"Click here for Demo Account\" button so the reviewer doesn't have to fill out a signup form.\n2. **Quantified impact in the bullet points:** \n   - Instead of: *\"Built a chat app with WebSockets.\"*\n   - Write: *\"Built a real-time messaging engine supporting 50 concurrent rooms with WebSocket heartbeats; reduced payload overhead by 40% using Protocol Buffers.\"*\n3. **Clean documentation:** An engaging 90-second Loom screen recording embedded in the GitHub README works like magic.",
        "created_at": "2026-10-07T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q3",
      "user_id": "u3",
      "student_name": "Riya Kapoor",
      "student_username": "riya.kapoor",
      "student_avatar": "/demo/avatar-2.svg",
      "student_college": "IIT Delhi",
      "student_course": "B.Tech Computer Science",
      "title": "How should students prepare for technical interviews?",
      "description": "Campus placement season is 6 months away. I know basic data structures, but I struggle when solving problems under time pressure and explaining my thought process out loud. What structured preparation strategy should I follow?",
      "category": "Placements and Interviews",
      "tags": [
        "TechnicalInterviews",
        "DSA",
        "MockInterviews",
        "Placements"
      ],
      "college_context": "IIT Delhi",
      "course_context": "B.Tech 3rd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_1",
        "alm_22"
      ],
      "created_at": "2026-10-06T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_3_1",
        "question_id": "q3",
        "author_id": "alm_22",
        "author_name": "Abhishek Pandey",
        "author_username": "abhishek.p.alumni",
        "author_avatar": "/demo/avatar-5.svg",
        "author_title": "Algorithms Specialist & CP Mentor",
        "author_company": "CodePulse Academy (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 38,
        "content": "With 6 months until campus placements, you have the sweet spot of time if you structure your daily routine intelligently.\n\nHere is the exact **4-Month Battle-Tested Strategy**:\n\n### Month 1 & 2: Pattern-Based Mastery (Not Random Grinding)\nDo not solve problems randomly. There are roughly 14 core coding patterns:\n1. Two Pointers & Sliding Window (e.g. Subarrays with given sum, trapping rain water)\n2. Fast & Slow Pointers (Cycle detection)\n3. Monotonic Stack & Queue (Next greater element, largest rectangle in histogram)\n4. Tree Traversals (BFS, DFS, lowest common ancestor, diameter)\n5. Graph Fundamentals (Connected components, topological sort, Dijkstra)\n6. Dynamic Programming: 0/1 Knapsack, unbounded knapsack, DP on strings (LCS, edit distance), DP on grids.\n\nSolve 10-15 problems per pattern. When you see a new question in an interview, you won't search your memory for that specific problem—you will instantly recognize the pattern.\n\n### Month 3: Time Constraint Training\nSet a timer for 25 minutes per LeetCode Medium. If you get stuck:\n- 0-5 mins: Understand constraints, clarify edge cases (empty array, negative numbers, overflow).\n- 5-15 mins: Write out logic on paper or dry-run test cases.\n- 15-25 mins: Code and run.\n\n### Month 4: Peer Mocks & Thinking Out Loud\nThe biggest reason students fail is silence. In an interview, **thinking in your head is a zero-score behavior**.\nFind a batchmate and take turns interviewing each other twice a week. You must verbalize:\n*\"My initial thought is a brute-force nested loop which takes O(N^2) time. Can we do better with a hash map to bring it down to O(N) space and time?\"*",
        "created_at": "2026-10-07T14:03:00.312Z",
        "replies": [
          {
            "id": "rep_3_1",
            "answer_id": "ans_3_1",
            "user_id": "u3",
            "author_name": "Riya Kapoor",
            "author_role": "student",
            "content": "The reminder to think out loud is gold. I always freeze because I feel I have to write the final optimal code immediately. Starting with brute force out loud takes the pressure off!",
            "created_at": "2026-10-08T14:03:00.312Z"
          }
        ]
      },
      {
        "id": "ans_3_2",
        "question_id": "q3",
        "author_id": "alm_1",
        "author_name": "Aarav Singhal",
        "author_username": "aarav.singhal.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Senior Staff Software Engineer",
        "author_company": "Northstar Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 22,
        "content": "Adding one interview technique: **Always write out your test cases before writing code.**\n\nWhen I interview candidates, I look for defensive engineering. If a candidate says: *\"Before I code, let me verify edge cases: What if the array is empty? What if all elements are identical? What if integers exceed 32-bit limits?\"* I immediately know this person writes reliable production code.",
        "created_at": "2026-10-08T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q4",
      "user_id": "u4",
      "student_name": "Dev Joshi",
      "student_username": "dev.joshi4",
      "student_avatar": "/demo/avatar-3.svg",
      "student_college": "BITS Pilani",
      "student_course": "B.Tech Electrical & Electronics",
      "title": "How can students find internships without prior experience?",
      "description": "I see internship listings requiring \"1-2 years of experience.\" As a student looking for my first summer internship, how do I get my foot in the door? Where should I apply and how should I pitch myself?",
      "category": "Internships",
      "tags": [
        "FirstInternship",
        "ColdEmail",
        "NoExperience",
        "EarlyCareer"
      ],
      "college_context": "BITS Pilani",
      "course_context": "B.Tech 2nd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_3",
        "alm_17"
      ],
      "created_at": "2026-10-07T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_4_1",
        "question_id": "q4",
        "author_id": "alm_3",
        "author_name": "Rohan Deshmukh",
        "author_username": "rohan.deshmukh.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "VP of Talent Acquisition & Campus Recruiting",
        "author_company": "Cobalt Talent Partners (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 29,
        "content": "Let me tell you an industry secret about job descriptions: **\"1-2 years experience required\" is almost always a wishlist, not a hard barrier for internships.**\n\nHiring managers write that to filter out people who have never written a line of code outside coursework. If you have built concrete projects, you have \"experience.\"\n\nHere is how you break in:\n\n### 1. Target Seed and Series-A Startups on Wellfound (AngelList)\nBig tech companies have rigid university recruitment windows. Early-stage startups (10-40 employees), on the other hand, hire whenever they have high workload. They care about one thing: **\"Can you write code that helps us ship this month?\"**\n\n### 2. The \"Value-First\" Cold Email\nNever send a copy-paste email saying *\"I am looking for an internship, please find attached resume.\"*\nInstead, research the startup's product:\n> *\"Hi [Founder/Engineering Lead], I tested your web app yesterday and noticed that the search query does not support keyboard navigation (Arrow Down / Enter). I created a small pull request / prototype showing how this can be implemented cleanly with custom hooks. Here is the demo link. I am a 2nd year CS student eager to contribute as an intern for the summer.\"*\n\nEven if they don't hire you for that exact role, 8 out of 10 engineering leads will reply to an email like that.",
        "created_at": "2026-10-08T14:03:00.312Z"
      },
      {
        "id": "ans_4_2",
        "question_id": "q4",
        "author_id": "alm_17",
        "author_name": "Pooja Hegde",
        "author_username": "pooja.hegde.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Early Career Program Manager & Tech Mentor",
        "author_company": "Catalyst Campus Works (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 20,
        "content": "Also look into formal open-source summer fellowship programs:\n- Google Summer of Code (GSoC)\n- Linux Foundation Mentorship (LFX)\n- Outreachy\n- MLH Fellowship\n\nThese programs are specifically designed for students without corporate experience. Contributing to open-source organizations gives you senior code reviews and real team collaboration that employers respect as much as corporate internships.",
        "created_at": "2026-10-09T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q5",
      "user_id": "u5",
      "student_name": "Meera Iyer",
      "student_username": "meera.iyer5",
      "student_avatar": "/demo/avatar-4.svg",
      "student_college": "University of Delhi",
      "student_course": "B.Sc Computer Science",
      "title": "What should a strong fresher resume contain?",
      "description": "I am drafting my first resume for off-campus and on-campus placement drives. Should it be 1 or 2 pages? How should I format my projects, education, and skills sections? What common mistakes turn recruiters off?",
      "category": "Resume and Portfolio",
      "tags": [
        "ResumeWriting",
        "FresherResume",
        "ATS",
        "Formatting"
      ],
      "college_context": "University of Delhi",
      "course_context": "B.Sc 3rd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_3",
        "alm_17"
      ],
      "created_at": "2026-10-07T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_5_1",
        "question_id": "q5",
        "author_id": "alm_3",
        "author_name": "Rohan Deshmukh",
        "author_username": "rohan.deshmukh.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "VP of Talent Acquisition & Campus Recruiting",
        "author_company": "Cobalt Talent Partners (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 36,
        "content": "As someone who has reviewed over 25,000 resumes, here are the absolute golden rules for a fresher resume:\n\n### 1. The One-Page Rule Is Sacred\nIf you are an undergraduate, your resume must be **exactly one single page**. A two-page resume for a fresher signals inability to synthesize and prioritize information.\n\n### 2. The Ideal Structure for Freshers:\n- **Header:** Full Name, City, Mobile, Email, Clickable LinkedIn, GitHub, and Portfolio URLs. No photo (for international/tech ATS), no marital status, no full street address.\n- **Education:** Degree, College, Graduation Year, CGPA (include if above 7.0/10 or 3.0/4.0).\n- **Technical Skills:** Categorize them!\n  - Languages: C++, Python, TypeScript, SQL\n  - Frameworks & Libraries: React, Node.js, Express, Tailwind CSS\n  - Developer Tools: Git, Docker, Postman, Linux\n- **Projects (The Heart of Your Resume):** 2 to 3 substantive projects. Use the Google XYZ formula:\n  - *\"Accomplished [X] as measured by [Y] by doing [Z]\"*\n  - Example: *\"Built an automated campus event alert bot using Node.js & Telegram API, notifying 1,200+ students within 500ms of schedule updates.\"*\n- **Achievements / Positions of Responsibility:** Hackathon wins, society lead roles, competitive coding ranks.\n\n### 3. What to Delete Immediately:\n- Objective statements (*\"Seeking a challenging position in an esteemed organization...\"* — this is useless fluff).\n- Progress bars for skills (*\"Java: 80%\"* — what does 80% Java even mean? ATS parsers also choke on these).\n- High school marks (nobody cares about 10th grade marks once you are in engineering).",
        "created_at": "2026-10-08T14:03:00.312Z"
      },
      {
        "id": "ans_5_2",
        "question_id": "q5",
        "author_id": "alm_17",
        "author_name": "Pooja Hegde",
        "author_username": "pooja.hegde.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Early Career Program Manager & Tech Mentor",
        "author_company": "Catalyst Campus Works (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 17,
        "content": "To ensure ATS parsing compatibility:\nUse a clean, single-column layout (LaTeX Jake's Resume format is widely regarded as the gold standard). Avoid multi-column sidebars, tables, and icons inside headers, as older ATS parsers read across columns and scramble your text.",
        "created_at": "2026-10-09T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q6",
      "user_id": "u6",
      "student_name": "Aditya Verma",
      "student_username": "aditya.verma6",
      "student_avatar": "/demo/avatar-5.svg",
      "student_college": "VIT Vellore",
      "student_course": "B.Tech Computer Science",
      "title": "How can a student build a portfolio without professional experience?",
      "description": "I do not have prior company internships. How can I build a credible developer portfolio that proves my competence to hiring managers? Does an online deployed website help more than just GitHub links?",
      "category": "Resume and Portfolio",
      "tags": [
        "Portfolio",
        "GitHub",
        "Showcase",
        "SideProjects"
      ],
      "college_context": "VIT Vellore",
      "course_context": "B.Tech 2nd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_10",
        "alm_13"
      ],
      "created_at": "2026-10-08T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_6_1",
        "question_id": "q6",
        "author_id": "alm_10",
        "author_name": "Karthik Sundaram",
        "author_username": "karthik.s.alumni",
        "author_avatar": "/demo/avatar-1.svg",
        "author_title": "Principal Full-Stack Architect",
        "author_company": "Nexus Digital Systems (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 25,
        "content": "Yes, a deployed personal website helps dramatically, but only if executed with care.\n\nA strong portfolio without prior work experience should demonstrate **craftsmanship, attention to detail, and technical maturity**. Here is how:\n\n### 1. The \"Two Star Projects\" Strategy\nInstead of listing 8 half-baked projects, focus on **two deep, fully finished applications**:\n- Both must be deployed with HTTPS on custom or clean subdomains (e.g. `.vercel.app`).\n- Provide seed/guest login credentials directly on the login page so reviewers don't have to register.\n- Ensure the mobile responsiveness is flawless—many engineering managers open portfolio links from their phones while commuting.\n\n### 2. Write Technical Case Studies, Not Just Repo Links\nOn your portfolio page, include a 2-minute read for each project:\n- **The Problem:** Why did you build this?\n- **The Architecture:** Show an SVG diagram of how the client talks to the API, cache, and database.\n- **The Hardest Bug:** Describe a difficult bug you ran into (e.g. race conditions in state updates) and how you solved it using Chrome DevTools or memory profiling.\n\nThis proves that you have problem-solving grit, which is the #1 trait hiring managers look for in entry-level hires.",
        "created_at": "2026-10-09T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q7",
      "user_id": "u7",
      "student_name": "Sana Patel",
      "student_username": "sana.patel7",
      "student_avatar": "/demo/avatar-6.svg",
      "student_college": "Delhi Technological University",
      "student_course": "B.Tech Software Engineering",
      "title": "Is competitive programming necessary for software engineering placements?",
      "description": "Some seniors say you must be a Codeforces Candidate Master or 5-star coder on CodeChef to get into top product companies, while others say building real-world projects and standard LeetCode is enough. What is the real truth for campus hiring?",
      "category": "Placements and Interviews",
      "tags": [
        "CompetitiveProgramming",
        "LeetCode",
        "CampusPlacements",
        "ProductCompanies"
      ],
      "college_context": "DTU",
      "course_context": "B.Tech 3rd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_1",
        "alm_22"
      ],
      "created_at": "2026-10-08T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_7_1",
        "question_id": "q7",
        "author_id": "alm_22",
        "author_name": "Abhishek Pandey",
        "author_username": "abhishek.p.alumni",
        "author_avatar": "/demo/avatar-5.svg",
        "author_title": "Algorithms Specialist & CP Mentor",
        "author_company": "CodePulse Academy (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 34,
        "content": "Let us bust this myth once and for all.\n\n### Is Competitive Programming (Codeforces/CodeChef) mandatory?\n**No, absolutely not.** \nAround 90% of software engineering roles in top product companies, high-growth startups, and MNCs do NOT require CP. What they require is **Data Structures and Algorithms (DSA) proficiency at a LeetCode Medium level**.\n\nHere is the distinction:\n- **Competitive Programming (CP):** High-speed mathematical problem solving, advanced number theory, segment trees, heavy ad-hoc puzzles. Great mental sport, but overkill for standard hiring.\n- **Interview DSA:** Clean code, standard data structures (Trees, Graphs, HashMaps, Heaps), and dynamic programming patterns.\n\n### Where CP Does Help:\nIf you are targeting ultra-selective high-frequency trading (HFT) firms or top-tier algorithmic contests, CP is very valuable. Also, CP gives you speed: you will clear the 60-minute online coding screening tests with ease.\n\n### What You Actually Need for Placements:\n1. Solve 150-250 curated LeetCode problems (e.g. NeetCode 150 or Blind 75).\n2. Write clean, idiomatic code with descriptive variable names.\n3. Understand time and space complexity deeply.",
        "created_at": "2026-10-09T14:03:00.312Z"
      },
      {
        "id": "ans_7_2",
        "question_id": "q7",
        "author_id": "alm_1",
        "author_name": "Aarav Singhal",
        "author_username": "aarav.singhal.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Senior Staff Software Engineer",
        "author_company": "Northstar Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 21,
        "content": "I will add a word of caution: competitive programmers often pick up bad coding habits that hurt them in technical interviews.\n\nIn CP, speed is everything, so people use single-letter variables (`int n, m, k;`), global arrays, and zero comments. In an actual software engineering interview, I evaluate candidates on **code readability, modularity, and error handling**. Make sure you practice writing clean code!",
        "created_at": "2026-10-10T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q8",
      "user_id": "u8",
      "student_name": "Vihaan Rao",
      "student_username": "vihaan.rao8",
      "student_avatar": "/demo/avatar-7.svg",
      "student_college": "Netaji Subhas University of Technology",
      "student_course": "B.Tech Computer Engineering",
      "title": "How should students choose between frontend and backend development?",
      "description": "I have dabbled in HTML/CSS/React and also written basic Express/Node.js servers with PostgreSQL. How do I decide whether to specialize in frontend, backend, or try to be full-stack early in my career?",
      "category": "Software Engineering and Technology",
      "tags": [
        "Frontend",
        "Backend",
        "FullStack",
        "CareerChoice"
      ],
      "college_context": "NSUT",
      "course_context": "B.Tech 2nd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_10",
        "alm_13",
        "alm_14"
      ],
      "created_at": "2026-10-09T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_8_1",
        "question_id": "q8",
        "author_id": "alm_10",
        "author_name": "Karthik Sundaram",
        "author_username": "karthik.s.alumni",
        "author_avatar": "/demo/avatar-1.svg",
        "author_title": "Principal Full-Stack Architect",
        "author_company": "Nexus Digital Systems (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 27,
        "content": "The beauty of software engineering is that you don't have to lock yourself into a permanent box right now.\n\nHere is how you evaluate where your natural affinities lie:\n\n### Choose Frontend if you enjoy:\n- Immediate visual and tactile feedback for every line of code you write.\n- Human-computer interaction, accessibility, UX animations, and responsiveness.\n- State management, client-side caching, rendering performance, and web vitals.\n- Bridging design prototypes in Figma into living browser experiences.\n\n### Choose Backend if you enjoy:\n- Data modeling, relational schema normalization, and indexing.\n- Scalability, high availability, caching tiers (Redis), and distributed consensus.\n- Asynchronous messaging pipelines, APIs, and security/authentication.\n- Measuring performance in query execution plans and database throughput.\n\n### My Recommendation for Students:\nBuild **one complete full-stack project** (e.g., React frontend + Node/Go backend + Postgres database). Understanding the complete flow—how a button click turns into an HTTP request, passes through middleware, queries a database, and returns JSON—makes you an exponentially better frontend OR backend specialist later.",
        "created_at": "2026-10-10T14:03:00.312Z"
      },
      {
        "id": "ans_8_2",
        "question_id": "q8",
        "author_id": "alm_13",
        "author_name": "Divya Chawla",
        "author_username": "divya.chawla.alumni",
        "author_avatar": "/demo/avatar-4.svg",
        "author_title": "Staff Frontend Platform Engineer",
        "author_company": "Lumina Web Platforms (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 18,
        "content": "A quick note to dispel a common misconception: **Frontend is not just styling CSS.** Modern frontend platform engineering involves compiler tooling, AST transformations, WebAssembly, memory leak profiling, and complex optimistic synchronization. Whichever path you choose, there is immense engineering depth!",
        "created_at": "2026-10-11T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q9",
      "user_id": "u9",
      "student_name": "Naina Singh",
      "student_username": "naina.singh9",
      "student_avatar": "/demo/avatar-0.svg",
      "student_college": "BITS Pilani",
      "student_course": "B.Tech Computer Science",
      "title": "What should students learn before applying for cloud engineering roles?",
      "description": "Cloud and DevOps are growing rapidly, but university courses rarely teach AWS, Docker, or Kubernetes. What foundational knowledge should a student acquire before applying for entry-level cloud or SRE positions?",
      "category": "Software Engineering and Technology",
      "tags": [
        "CloudComputing",
        "DevOps",
        "AWS",
        "Docker",
        "SRE"
      ],
      "college_context": "BITS Pilani",
      "course_context": "B.Tech 3rd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_5",
        "alm_18",
        "alm_26"
      ],
      "created_at": "2026-10-09T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_9_1",
        "question_id": "q9",
        "author_id": "alm_5",
        "author_name": "Vikram Malhotra",
        "author_username": "vikram.m.alumni",
        "author_avatar": "/demo/avatar-4.svg",
        "author_title": "Staff DevOps & Cloud Infrastructure Architect",
        "author_company": "Apex Cloud Systems (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 28,
        "content": "Cloud engineering is one of the most rewarding career paths, but many students make the mistake of just memorizing AWS console menus without understanding the underlying systems.\n\nHere are the **4 Foundational Pillars** you must master:\n\n### 1. Linux Internals and Networking\nCloud is simply someone else's Linux servers.\n- Learn bash scripting, file permissions (`chmod`, `chown`), systemd services, and process monitoring (`top`, `htop`, `lsof`).\n- Understand networking: DNS resolution, TCP three-way handshake, Subnets, CIDR blocks, NAT Gateways, and SSL/TLS handshakes.\n\n### 2. Containerization with Docker\n- Learn how to write optimized, multi-stage Dockerfiles.\n- Understand the difference between image layers, containers, and volumes.\n- Know why you shouldn't run containers as root users.\n\n### 3. Infrastructure as Code (Terraform)\nDo not click around the AWS UI manually. Write a Terraform script that provisions a VPC, an EC2 instance, and an S3 bucket with proper IAM roles.\n\n### 4. CI/CD Automation\nCreate a GitHub Actions workflow that automatically runs linting, tests, builds a Docker image, pushes it to Docker Hub, and deploys it to a VM whenever you merge to `main`.\n\nIf you have a GitHub repository demonstrating automated Terraform provisioning and a CI/CD pipeline, you will easily outcompete candidates with generic resumes.",
        "created_at": "2026-10-10T14:03:00.312Z"
      },
      {
        "id": "ans_9_2",
        "question_id": "q9",
        "author_id": "alm_26",
        "author_name": "Harshita Rao",
        "author_username": "harshita.rao.alumni",
        "author_avatar": "/demo/avatar-1.svg",
        "author_title": "Site Reliability Engineer (SRE)",
        "author_company": "CloudGate Infrastructure (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 16,
        "content": "Learn observability! Deploy an open-source app and configure Prometheus + Grafana dashboards to monitor CPU, memory, and HTTP 5xx error rates. When you can talk about Service Level Objectives (SLOs), error budgets, and post-mortems in an interview, entry-level recruiters will be blown away.",
        "created_at": "2026-10-11T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q10",
      "user_id": "u10",
      "student_name": "Rohan Joshi",
      "student_username": "rohan.joshi10",
      "student_avatar": "/demo/avatar-1.svg",
      "student_college": "IIT Delhi",
      "student_course": "B.Tech Mathematics & Computing",
      "title": "How can students prepare for an AI or machine learning career?",
      "description": "With all the hype around Generative AI, what does it genuinely take to build a career in AI/ML right after college? Is a Master degree mandatory, and what math/coding foundations should I focus on as an undergraduate?",
      "category": "Software Engineering and Technology",
      "tags": [
        "MachineLearning",
        "AI",
        "DeepLearning",
        "MathPrereqs",
        "CareerPath"
      ],
      "college_context": "IIT Delhi",
      "course_context": "B.Tech 2nd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_4",
        "alm_21"
      ],
      "created_at": "2026-10-10T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_10_1",
        "question_id": "q10",
        "author_id": "alm_4",
        "author_name": "Ananya Krishnamurthy",
        "author_username": "ananya.k.alumni",
        "author_avatar": "/demo/avatar-3.svg",
        "author_title": "Senior Machine Learning Research Engineer",
        "author_company": "Zenith AI Labs (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 35,
        "content": "There is a huge gap between what AI hype on social media looks like and what an entry-level AI engineer actually does day-to-day.\n\nHere is the realistic blueprint:\n\n### 1. Mathematics Is Your Competitive Moat\nDon't skip the math:\n- **Linear Algebra:** Vectors, matrices, dot products, eigenvalues/eigenvectors, SVD (this is how embeddings and dimensionality reduction work).\n- **Calculus:** Partial derivatives, chain rule (how backpropagation actually functions).\n- **Probability & Statistics:** Distributions, Bayes theorem, maximum likelihood estimation, hypothesis testing.\n\n### 2. Implement Algorithms from Scratch First\nBefore calling `model.fit()` or using high-level libraries:\n- Code linear regression, logistic regression, and a 2-layer neural network using pure NumPy.\n- Compute the gradients by hand on paper, then verify with autograd in PyTorch.\nThis builds an intuition that protects you from being a generic \"API caller.\"\n\n### 3. Work with Real-World Messy Data\nReal data doesn't come clean like Kaggle CSVs. Build projects where you:\n- Scrape data, clean corrupted records, handle class imbalances, and tokenize text.\n- Train an open-source LLM (like Llama or Mistral) on custom domain data using LoRA/QLoRA.\n- Measure evaluation metrics rigorously (BLEU, ROUGE, Precision/Recall, latency per token).\n\nA Master's degree is helpful for core research labs, but strong engineering capability + genuine ML fundamentals will land you high-impact ML engineering roles right after B.Tech.",
        "created_at": "2026-10-11T14:03:00.312Z"
      },
      {
        "id": "ans_10_2",
        "question_id": "q10",
        "author_id": "alm_21",
        "author_name": "Shweta Kulkarni",
        "author_username": "shweta.k.alumni",
        "author_avatar": "/demo/avatar-4.svg",
        "author_title": "Senior NLP & LLM Systems Engineer",
        "author_company": "NeuralCraft Solutions (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 22,
        "content": "The biggest demand in industry right now is **Applied AI & MLOps**. Learn how to build production RAG (Retrieval-Augmented Generation) systems with vector databases (Pinecone, Chroma, Qdrant), write evaluation harnesses, and optimize inference endpoints using quantization and ONNX.",
        "created_at": "2026-10-12T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q11",
      "user_id": "u11",
      "student_name": "Anika Sharma",
      "student_username": "anika.sharma11",
      "student_avatar": "/demo/avatar-2.svg",
      "student_college": "VIT Vellore",
      "student_course": "B.Tech Information Security",
      "title": "How should students approach cybersecurity as beginners?",
      "description": "Cybersecurity seems intimidating with so many sub-fields (SOC, red teaming, cryptography, cloud security, forensics). What is the best path for a beginner to gain hands-on security skills while in college?",
      "category": "Software Engineering and Technology",
      "tags": [
        "Cybersecurity",
        "InfoSec",
        "PenTesting",
        "BeginnerRoadmap"
      ],
      "college_context": "VIT Vellore",
      "course_context": "B.Tech 2nd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_7"
      ],
      "created_at": "2026-10-10T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_11_1",
        "question_id": "q11",
        "author_id": "alm_7",
        "author_name": "Kavita Sen",
        "author_username": "kavita.sen.alumni",
        "author_avatar": "/demo/avatar-6.svg",
        "author_title": "Senior Cybersecurity Analyst & Penetration Tester",
        "author_company": "Beacon Cyber Labs (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 31,
        "content": "Cybersecurity is unique because you cannot learn it purely from textbooks—you need hands-on puzzle solving.\n\nHere is the exact progression I recommend for college students:\n\n### Phase 1: Linux & Command Line Mastery (Weeks 1-3)\nPlay **OverTheWire: Bandit**. It is a gamified security challenge that forces you to connect via SSH, inspect hidden files, decompress nested tar archives, and understand Linux permissions. Complete levels 1 through 25.\n\n### Phase 2: Fundamental Hands-On Lab (Weeks 4-8)\nSign up for **TryHackMe** and follow the *Pre-Security* and *Complete Beginner* learning paths. You will learn:\n- How networks operate (Nmap port scanning, Wireshark packet capture).\n- How web applications work under the hood (HTTP headers, cookies, sessions).\n\n### Phase 3: Web Security Exploitation (Weeks 9-16)\nWork through **PortSwigger Web Security Academy** (completely free, created by the makers of Burp Suite). Master the OWASP Top 10 vulnerabilities:\n- SQL Injection (SQLi)\n- Cross-Site Scripting (XSS)\n- Broken Object Level Authorization (BOLA/IDOR)\n- Cross-Site Request Forgery (CSRF)\n\n### Phase 4: Join College CTF Competitions\nParticipate in beginner Capture The Flag (CTF) events on CTFtime with your college mates. CTFs give you practical experience and a community of security-minded peers.",
        "created_at": "2026-10-11T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q12",
      "user_id": "u12",
      "student_name": "Dhruv Verma",
      "student_username": "dhruv.verma12",
      "student_avatar": "/demo/avatar-3.svg",
      "student_college": "University of Delhi",
      "student_course": "B.A. Vocational Studies",
      "title": "What is the difference between an internship and an apprenticeship?",
      "description": "I see companies offering both internships (usually 2-3 months) and apprenticeships (6-12 months). How do they differ in terms of learning, stipends, pre-placement offers (PPOs), and long-term career benefits?",
      "category": "Career Guidance",
      "tags": [
        "InternshipVsApprenticeship",
        "CareerAdvice",
        "PPO",
        "Training"
      ],
      "college_context": "DU",
      "course_context": "3rd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_3",
        "alm_17"
      ],
      "created_at": "2026-10-11T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_12_1",
        "question_id": "q12",
        "author_id": "alm_3",
        "author_name": "Rohan Deshmukh",
        "author_username": "rohan.deshmukh.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "VP of Talent Acquisition & Campus Recruiting",
        "author_company": "Cobalt Talent Partners (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 24,
        "content": "Both internships and apprenticeships are fantastic stepping stones, but they serve different career needs:\n\n### 1. Duration and Commitment:\n- **Internships:** Usually short-term (8 to 12 weeks during summer or 6 months during the final college semester). Fast-paced, focused on delivering a specific project.\n- **Apprenticeships:** Typically longer (6 to 12 months, or sometimes up to 2 years). Structured like an intensive corporate residency with dedicated training modules.\n\n### 2. Mentorship & Learning Curve:\n- **Internship:** You are expected to hit the ground running with existing skills. Mentorship exists, but you must be proactive.\n- **Apprenticeship:** Heavy emphasis on foundational skill development. Many large firms and government initiatives design apprenticeships to cross-train students into technical roles.\n\n### 3. PPO (Pre-Placement Offer) Conversion:\n- Both can convert into full-time roles, but internships often have a more direct conversion evaluation right at the end of the 8-10 week period.\n\n**My Advice:** If you are a 3rd-year student on summer break, target an internship. If you are graduating and don't yet have a campus placement offer, a 6-12 month apprenticeship at a reputable firm is 100x better than an empty gap on your resume.",
        "created_at": "2026-10-12T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q13",
      "user_id": "u13",
      "student_name": "Tara Mehta",
      "student_username": "tara.mehta13",
      "student_avatar": "/demo/avatar-4.svg",
      "student_college": "Delhi Technological University",
      "student_course": "B.Tech Civil Engineering",
      "title": "How can students improve communication and interview confidence?",
      "description": "I often freeze during HR and behavioral rounds even when I know the answer. English is not my first language, and I feel self-conscious. How can I systematically improve my confidence and articulate my thoughts clearly?",
      "category": "Communication and Professional Skills",
      "tags": [
        "Communication",
        "InterviewConfidence",
        "BehavioralRounds",
        "SpeakingSkills"
      ],
      "college_context": "DTU",
      "course_context": "B.Tech 3rd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_12",
        "alm_19",
        "alm_23"
      ],
      "created_at": "2026-10-11T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_13_1",
        "question_id": "q13",
        "author_id": "alm_12",
        "author_name": "Arjun Mathur",
        "author_username": "arjun.mathur.alumni",
        "author_avatar": "/demo/avatar-3.svg",
        "author_title": "Management Consultant",
        "author_company": "Pinnacle Strategy Group (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 33,
        "content": "First, please know this: **Communication is a learnable skill, not an innate personality trait.** In engineering interviews, recruiters do NOT expect theatrical flair or British accents; they want **structure and clarity**.\n\nHere is how to master behavioral and HR rounds:\n\n### 1. The STAR Framework (Your Magic Bullet)\nFor any question starting with *\"Tell me about a time when...\"*, structure your answer strictly into 4 parts:\n- **S - Situation (15%):** Set the context in 2 sentences. *\"During our 3rd-year robotics project, our team had 48 hours before the regional qualifier.\"*\n- **T - Task (15%):** What was the specific obstacle? *\"Our motor driver IC burned out, and our supplier was closed.\"*\n- **A - Action (50%):** What did YOU specifically do? (Use \"I\", not just \"we\"). *\"I redesigned the circuit diagram, repurposed a spare Arduino driver board from another lab, and rewrote the PWM frequency code.\"*\n- **R - Result (20%):** The measurable outcome and learning. *\"The robot completed the qualification run with the 4th fastest time, and we advanced to the finals.\"*\n\n### 2. Prepare 5 Multi-Purpose Stories\nYou don't need 30 different stories. Prepare 5 rich experiences from college life (a difficult project, a team disagreement, a major failure, a tight deadline, a leadership moment). Each of these 5 stories can answer 5 different interview questions.\n\n### 3. The 3-Second Pause Rule\nWhen asked a question, do not blurt out the first thought that comes to your mind. Say: *\"That is a great question. Let me take 3 seconds to organize my thoughts.\"* That brief pause signals composure, maturity, and confidence.",
        "created_at": "2026-10-12T14:03:00.312Z"
      },
      {
        "id": "ans_13_2",
        "question_id": "q13",
        "author_id": "alm_23",
        "author_name": "Meenal Agarwal",
        "author_username": "meenal.a.alumni",
        "author_avatar": "/demo/avatar-6.svg",
        "author_title": "People & Culture Business Partner",
        "author_company": "Novus Scale Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 22,
        "content": "A psychological trick to calm interview jitters: **The Physiological Sigh.** Right before opening your Zoom or entering the interview room, take two deep inhales through your nose, followed by a long, slow exhale through your mouth. Doing this twice instantly resets autonomic nervous system arousal and lowers your heart rate.",
        "created_at": "2026-10-13T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q14",
      "user_id": "u14",
      "student_name": "Yash Kapoor",
      "student_username": "yash.kapoor14",
      "student_avatar": "/demo/avatar-5.svg",
      "student_college": "Netaji Subhas University of Technology",
      "student_course": "B.Tech Mechanical Engineering",
      "title": "How should students prepare for aptitude tests?",
      "description": "Many on-campus placement tests use quantitative aptitude, logical reasoning, and verbal ability as an initial cutoff filter. What resources, daily habits, and mock test routines actually work to clear this stage?",
      "category": "Placements and Interviews",
      "tags": [
        "AptitudeTests",
        "QuantitativePrep",
        "LogicalReasoning",
        "CampusDrives"
      ],
      "college_context": "NSUT",
      "course_context": "B.Tech 3rd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_9",
        "alm_22"
      ],
      "created_at": "2026-10-12T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_14_1",
        "question_id": "q14",
        "author_id": "alm_22",
        "author_name": "Abhishek Pandey",
        "author_username": "abhishek.p.alumni",
        "author_avatar": "/demo/avatar-5.svg",
        "author_title": "Algorithms Specialist & CP Mentor",
        "author_company": "CodePulse Academy (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 26,
        "content": "Aptitude rounds are high-volume elimination filters. Companies use them because they get 2,000 applicants for 30 positions and need to cut the pool to 200 within an hour.\n\nHere is the high-yield preparation formula:\n\n### 1. Master the Top 8 Quantitative Topics (The 80/20 Rule)\n80% of test questions come from these specific topics:\n- Percentages & Profit/Loss\n- Time, Speed & Distance (Trains, Boats & Streams)\n- Time & Work (Pipes & Cisterns)\n- Ratio, Proportion & Mixtures\n- Simple & Compound Interest\n- Permutations, Combinations & Probability\n- Number System (Remainders, Divisibility rules)\n- Data Interpretation (Tables, Bar graphs, Pie charts)\n\n### 2. Time Boxing and the Skip Strategy\nIn aptitude tests, **all questions carry equal marks, but not equal time**.\n- If a question takes more than 60-75 seconds to solve, mark it for review and move on immediately.\n- Never get emotionally attached to solving a question just because you studied that topic.\n\n### 3. Practice with a Timer, Not Just Workbooks\nPracticing without a timer is useless. Take 30-minute sectional mock tests on platforms like IndiaBIX, PrepInsta, or standard CAT-level material. Analyze which question types you consistently get wrong and drill their shortcut techniques.",
        "created_at": "2026-10-13T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q15",
      "user_id": "u15",
      "student_name": "Ishita Rao",
      "student_username": "ishita.rao15",
      "student_avatar": "/demo/avatar-6.svg",
      "student_college": "BITS Pilani",
      "student_course": "B.Tech Chemical Engineering",
      "title": "How can students network professionally without feeling awkward?",
      "description": "Reaching out to alumni and industry professionals on LinkedIn often feels like unsolicited begging or transactional spam. How do you approach people naturally, build genuine relationships, and ask for mentorship or referrals?",
      "category": "Communication and Professional Skills",
      "tags": [
        "Networking",
        "LinkedInEtiquette",
        "Mentorship",
        "Referrals"
      ],
      "college_context": "BITS Pilani",
      "course_context": "2nd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_3",
        "alm_17"
      ],
      "created_at": "2026-10-12T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_15_1",
        "question_id": "q15",
        "author_id": "alm_17",
        "author_name": "Pooja Hegde",
        "author_username": "pooja.hegde.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Early Career Program Manager & Tech Mentor",
        "author_company": "Catalyst Campus Works (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 35,
        "content": "Networking feels awkward only when it is purely transactional—like messaging someone you never talked to with: *\"Hi sir, please refer me for Job ID 12345.\"* That feels like spam because it is spam.\n\nHere is how you build genuine, warm relationships with alumni:\n\n### 1. The \"Common Ground\" Approach\nStart with your shared alma mater:\n> *\"Hi [Name], I am currently a 3rd-year CS student at [College]. I came across your profile while researching distributed systems engineering and saw your post on database migrations. It was super insightful! As someone hoping to follow a similar career trajectory, could I ask one specific question about what you found most useful during your college-to-work transition?\"*\n\nNotice:\n- You didn't ask for a job.\n- You respected their time by asking ONE specific question.\n- You acknowledged their work genuinely.\n\n### 2. Close the Loop (The Secret to Long-Term Mentorship)\nIf an alumnus gives you advice or book recommendations, **message them back 4 weeks later**:\n> *\"Hi [Name], just wanted to let you know that I read the system design chapter you recommended and implemented the caching strategy in my semester project. It worked great! Thank you again for pointing me in the right direction.\"*\n\nAlmost nobody closes the loop. When you do, that alumnus will remember you, and when a job opening comes up on their team, they will proactively think of you.",
        "created_at": "2026-10-13T14:03:00.312Z"
      },
      {
        "id": "ans_15_2",
        "question_id": "q15",
        "author_id": "alm_3",
        "author_name": "Rohan Deshmukh",
        "author_username": "rohan.deshmukh.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "VP of Talent Acquisition & Campus Recruiting",
        "author_company": "Cobalt Talent Partners (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 21,
        "content": "When you DO ask for a referral, make it effortless for the employee:\nSend:\n1. Exact Job ID & Link\n2. Clean 1-page PDF Resume link\n3. A 3-bullet summary of why your skills match that specific role.\nEmployees are happy to refer good candidates because companies pay referral bonuses, but you must do the heavy lifting for them!",
        "created_at": "2026-10-14T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q16",
      "user_id": "u16",
      "student_name": "Arjun Nair",
      "student_username": "arjun.nair16",
      "student_avatar": "/demo/avatar-7.svg",
      "student_college": "IIT Delhi",
      "student_course": "B.Tech Electrical Engineering",
      "title": "How should students decide whether to pursue a master's degree?",
      "description": "I am torn between taking an industry job offer after graduation versus pursuing an MS/M.Tech immediately. What factors should I weigh—work experience first vs straight higher education?",
      "category": "Higher Education",
      "tags": [
        "MastersDegree",
        "MSAbroad",
        "MTech",
        "CareerDecisions"
      ],
      "college_context": "IIT Delhi",
      "course_context": "B.Tech 4th Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_4",
        "alm_15"
      ],
      "created_at": "2026-10-13T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_16_1",
        "question_id": "q16",
        "author_id": "alm_15",
        "author_name": "Isha Mehra",
        "author_username": "isha.mehra.alumni",
        "author_avatar": "/demo/avatar-6.svg",
        "author_title": "Doctoral Scholar & Higher Education Mentor",
        "author_company": "Global Academic Research Institute (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 32,
        "content": "This is one of the most critical crossroads in a student's final year. Let us break it down clearly:\n\n### When to Pursue a Master's Immediately:\n1. **Specialized Research or Frontier Fields:** If you want to work on deep AI research, chip design/VLSI, quantum computing, or computational biology, a bachelor's degree simply doesn't cover enough theoretical depth.\n2. **Academic or Research Career:** If your ultimate goal is a Ph.D. or professorship, going straight into graduate school keeps your momentum alive.\n3. **Changing Specialization:** If you did your B.Tech in Mechanical or Civil and want to transition to Computer Science or Data Science, a Master's provides a legitimate institutional reset.\n\n### When to Work in Industry for 1-2 Years First:\n1. **General Software Engineering:** Most software roles value shipping production code, working in cross-functional teams, and debugging live outages more than academic coursework.\n2. **Financial Prudence:** A Master's degree abroad often costs $60K-$100K in loans. Working 2 years lets you save an emergency fund, pay off undergraduate loans, and evaluate your true interests.\n3. **Stronger Grad School Applications:** Having 1-2 years of real engineering experience makes your Statement of Purpose (SOP) much more compelling than a generic fresher application.",
        "created_at": "2026-10-14T14:03:00.312Z"
      },
      {
        "id": "ans_16_2",
        "question_id": "q16",
        "author_id": "alm_4",
        "author_name": "Ananya Krishnamurthy",
        "author_username": "ananya.k.alumni",
        "author_avatar": "/demo/avatar-3.svg",
        "author_title": "Senior Machine Learning Research Engineer",
        "author_company": "Zenith AI Labs (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 20,
        "content": "I worked for 14 months before diving into advanced ML research. That year in industry completely changed my perspective—I learned how production systems differ from toy Jupyter notebooks, and that clarity made my graduate studies 2x more focused and productive.",
        "created_at": "2026-10-14T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q17",
      "user_id": "u17",
      "student_name": "Sana Joshi",
      "student_username": "sana.joshi17",
      "student_avatar": "/demo/avatar-0.svg",
      "student_college": "University of Delhi",
      "student_course": "B.Sc Economics",
      "title": "What should students consider before applying abroad for higher education?",
      "description": "Going abroad for a master degree involves massive financial loans, visa considerations, and post-study work regulations. What are the key realities students should evaluate before committing to foreign universities?",
      "category": "Higher Education",
      "tags": [
        "StudyAbroad",
        "StudentLoans",
        "VisaRules",
        "GlobalCareers"
      ],
      "college_context": "DU",
      "course_context": "Final Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_15"
      ],
      "created_at": "2026-10-13T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_17_1",
        "question_id": "q17",
        "author_id": "alm_15",
        "author_name": "Isha Mehra",
        "author_username": "isha.mehra.alumni",
        "author_avatar": "/demo/avatar-6.svg",
        "author_title": "Doctoral Scholar & Higher Education Mentor",
        "author_company": "Global Academic Research Institute (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 29,
        "content": "Studying abroad can be life-changing, but please do not take a 70-lakh loan based on Instagram reels.\n\nHere is the realistic checklist you must evaluate:\n\n### 1. Total Cost of Attendance vs Loan Repayment\nFactor in tuition, health insurance, housing, winter clothes, and living costs. \nCalculate the monthly loan EMI: if you borrow $60,000 at 9-11% interest, your monthly EMI after graduation could be $800-$1,200. You need to ensure the local starting salary comfortably covers living expenses plus EMI.\n\n### 2. Post-Study Work Visa Policies\n- USA: STEM OPT allows 3 years of work authorization without H-1B lottery dependency initially.\n- UK: 2-year Graduate Route visa, but company sponsorship thresholds have risen recently.\n- Germany/EU: 18-month job seeker visa with strong demand for engineers, but learning conversational German is essential for long-term integration.\n\n### 3. University Brand vs Curriculum\nDo not pick a university purely for rank. Check:\n- Are professors actively publishing in areas you care about?\n- Does the location have a local tech hub (e.g. Bay Area, Austin, Seattle, Munich, Dublin) where companies hire locally?",
        "created_at": "2026-10-14T14:03:00.312Z"
      }
    ]
  },
  {
    "question": {
      "id": "q18",
      "user_id": "u18",
      "student_name": "Vihaan Verma",
      "student_username": "vihaan.verma18",
      "student_avatar": "/demo/avatar-1.svg",
      "student_college": "VIT Vellore",
      "student_course": "B.Tech Computer Science",
      "title": "How can students identify legitimate internship opportunities?",
      "description": "There are so many companies asking students to pay for \"training-cum-internships\" or offering unpaid work with vague promises of certificates. What are the major red flags to avoid fake or exploitative internships?",
      "category": "Internships",
      "tags": [
        "InternshipScams",
        "UnpaidInternships",
        "CareerSafety",
        "RedFlags"
      ],
      "college_context": "VIT Vellore",
      "course_context": "2nd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_3",
        "alm_17"
      ],
      "created_at": "2026-10-14T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_18_1",
        "question_id": "q18",
        "author_id": "alm_17",
        "author_name": "Pooja Hegde",
        "author_username": "pooja.hegde.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Early Career Program Manager & Tech Mentor",
        "author_company": "Catalyst Campus Works (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 36,
        "content": "Every summer, thousands of vulnerable students get scammed by fraudulent training institutes posing as companies.\n\nHere are the **Non-Negotiable Red Flags**:\n\n### 1. ANY Upfront Fee = 100% Scam\nIf anyone asks for a \"registration fee,\" \"laptop security deposit,\" \"certificate processing charge,\" or \"training module fee\"—**RUN**. Real companies pay you for your work; they never charge you to work for them.\n\n### 2. The WhatsApp / Telegram Direct Offer\nLegitimate companies do not extend software engineering internship offers via a random WhatsApp message or Telegram group without an interview, coding test, or conversation with a hiring manager.\n\n### 3. Free \"Unpaid\" Work with Full-Time Expectations\nIf a startup expects you to work 40 hours a week for 3 months with zero stipend and no clear senior engineer mentorship, you are not interning—you are providing free labor. Look for paid roles or open source contributions instead.",
        "created_at": "2026-10-15T14:03:00.312Z"
      },
      {
        "id": "ans_18_2",
        "question_id": "q18",
        "author_id": "alm_3",
        "author_name": "Rohan Deshmukh",
        "author_username": "rohan.deshmukh.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "VP of Talent Acquisition & Campus Recruiting",
        "author_company": "Cobalt Talent Partners (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 23,
        "content": "How to verify a company:\n1. Look up the company domain on LinkedIn. Does it have real employees with verified work histories?\n2. Check email headers: is the email coming from `@company.com` or a generic `@gmail.com` address?\n3. Check MCA / corporate registrar records for registered corporate entities.",
        "created_at": "2026-10-15T14:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q19",
      "user_id": "u19",
      "student_name": "Naina Rao",
      "student_username": "naina.rao19",
      "student_avatar": "/demo/avatar-2.svg",
      "student_college": "Delhi Technological University",
      "student_course": "B.Tech Computer Science",
      "title": "How can a student evaluate a startup job offer?",
      "description": "I have an offer from an early-stage startup (Series A, 15 people) offering lower base salary but ESOPs, and an offer from an established IT consultancy offering stable pay. How should a fresher evaluate the risk vs learning tradeoff?",
      "category": "Career Guidance",
      "tags": [
        "StartupOffer",
        "ESOPs",
        "MNCvsStartup",
        "OfferEvaluation"
      ],
      "college_context": "DTU",
      "course_context": "4th Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_8",
        "alm_25"
      ],
      "created_at": "2026-10-14T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_19_1",
        "question_id": "q19",
        "author_id": "alm_25",
        "author_name": "Aakash Bhardwaj",
        "author_username": "aakash.b.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Startup Operator & Venture Associate",
        "author_company": "Frontier Seed Capital (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 30,
        "content": "Evaluating an early-stage startup offer requires understanding risk and compensation mechanics:\n\n### 1. Ask About Runway (Politely but Firmly)\nDuring your conversation with the founders, it is completely standard to ask:\n*\"What is the current runway of the company, and what are the milestones for the next funding round?\"*\nA healthy startup will transparently answer: *\"We have 18-24 months of runway with our Series A round.\"* If they get defensive, that is a red flag.\n\n### 2. Demystifying ESOPs (Employee Stock Options)\nNever accept lower salary just because someone says *\"We are giving you 50,000 rupees worth of stock.\"*\nAsk for the details:\n- What percentage of the company does this grant represent?\n- What is the vesting schedule? (Industry standard is a **4-year vest with a 1-year cliff**).\n- What is the exercise price (strike price)?\nAssume ESOPs have a high probability of going to zero, but offer asymmetric upside if the company succeeds. Make sure your base cash salary comfortably covers your rent, food, and living expenses.",
        "created_at": "2026-10-15T14:03:00.313Z"
      },
      {
        "id": "ans_19_2",
        "question_id": "q19",
        "author_id": "alm_8",
        "author_name": "Aditya Banerjee",
        "author_username": "aditya.b.alumni",
        "author_avatar": "/demo/avatar-7.svg",
        "author_title": "Founder & CEO",
        "author_company": "Velocity Mobility (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 19,
        "content": "As a founder, I will say this: at an established MNC, you might maintain a small module of a legacy system. At a 15-person startup, you will design databases, ship features directly to users, and talk to customers in your first month. If you crave rapid ownership, the startup learning speed is unmatched.",
        "created_at": "2026-10-15T14:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q20",
      "user_id": "u20",
      "student_name": "Rohan Sharma",
      "student_username": "rohan.sharma20",
      "student_avatar": "/demo/avatar-3.svg",
      "student_college": "BITS Pilani",
      "student_course": "B.Tech Computer Science",
      "title": "What should students know before launching a startup?",
      "description": "My roommate and I have built an MVP for a campus marketplace. We are debating whether to register a private limited company, raise angel funding, or bootstrap while still in college. What are the essential early lessons?",
      "category": "Entrepreneurship and Startups",
      "tags": [
        "StartupFounder",
        "Bootstrapping",
        "StudentEntrepreneurs",
        "MVP"
      ],
      "college_context": "BITS Pilani",
      "course_context": "3rd Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_8",
        "alm_25"
      ],
      "created_at": "2026-10-15T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_20_1",
        "question_id": "q20",
        "author_id": "alm_8",
        "author_name": "Aditya Banerjee",
        "author_username": "aditya.b.alumni",
        "author_avatar": "/demo/avatar-7.svg",
        "author_title": "Founder & CEO",
        "author_company": "Velocity Mobility (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 34,
        "content": "Building a startup in college was the most intense and rewarding decision of my life. Here are the 3 hard truths I wish someone had told me early:\n\n### 1. Do Not Register a Company on Day One\nDo not spend money on lawyers, trademarking, or company incorporation until you have verified **willingness to pay**.\nMost student founders build an app for 6 months, incorporate a company, print business cards, and then realize nobody actually wants the product.\nValidate first: can you get 50 students to use a Google Sheet or WhatsApp group to solve the problem today?\n\n### 2. Co-Founder Alignment\nFriendships can be strained under startup pressure. Have the uncomfortable conversations now:\n- What happens if one person gets a high-paying placement offer in 4th year and wants to drop out of the startup?\n- How is equity split? (Equal splits among co-founders working equal hours is standard, but put in a 3-4 year vesting schedule so someone can't leave with 50% of the company after 2 months).\n\n### 3. Talk to Customers Every Single Day\nFall in love with the problem, not your initial solution. If students say your checkout flow is too complicated, don't defend it—listen, simplify, and iterate.",
        "created_at": "2026-10-15T14:03:00.313Z"
      },
      {
        "id": "ans_20_2",
        "question_id": "q20",
        "author_id": "alm_25",
        "author_name": "Aakash Bhardwaj",
        "author_username": "aakash.b.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Startup Operator & Venture Associate",
        "author_company": "Frontier Seed Capital (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 22,
        "content": "Look for campus entrepreneurship cells (E-Cells) and incubators. Many universities have government-backed grants (like NIDHI-PRAYAS or student innovation funds) that provide $5,000-$15,000 in equity-free grant money. That is the best capital to build your initial hardware or software prototype without diluting ownership!",
        "created_at": "2026-10-16T14:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q21",
      "user_id": "u21",
      "student_name": "Anika Kapoor",
      "student_username": "anika.kapoor21",
      "student_avatar": "/demo/avatar-4.svg",
      "student_college": "University of Delhi",
      "student_course": "B.A. Multimedia & Web Design",
      "title": "How can design students build a compelling UI/UX portfolio?",
      "description": "Recruiters often criticize student UI/UX portfolios for showing pretty Dribbble mockups without explaining the problem, user research, or business constraints. What does a hiring manager really want to see in a design case study?",
      "category": "Design and Product Management",
      "tags": [
        "UIUXDesign",
        "DesignPortfolio",
        "CaseStudy",
        "Figma"
      ],
      "college_context": "DU",
      "course_context": "3rd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_2"
      ],
      "created_at": "2026-10-15T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_21_1",
        "question_id": "q21",
        "author_id": "alm_2",
        "author_name": "Priya Nair",
        "author_username": "priya.nair.alumni",
        "author_avatar": "/demo/avatar-1.svg",
        "author_title": "Lead UI/UX Product Designer",
        "author_company": "Starlight Studio Design (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 37,
        "content": "As a design lead reviewing intern portfolios, let me be blunt: **Dribbble visual shots are not UX case studies.**\n\nPretty gradients and rounded cards don't tell me if you can solve real user problems. Here is the anatomy of a case study that actually gets interviews:\n\n### 1. The Problem Statement & Business Context\n- What was broken? Who is the target user?\n- What were the constraints? (e.g. mobile-only, low-bandwidth internet, short attention span).\n\n### 2. User Research & Insights\nDon't just show generic personas named \"Sarah, 22\". Show actual user friction:\n*\"We interviewed 8 college students and found that 6 of them abandoned the hostel laundry booking app because there was no slot reservation countdown.\"*\n\n### 3. The Ugly Iterations (Show Your Scrap Paper!)\nThis is the most important section. Show your messy wireframes, your failed layout tests, and explain:\n*\"We initially tried a modal drawer, but usability testing revealed users missed the confirmation button. We pivoted to a sticky bottom bar.\"*\nHiring managers hire your **decision-making process**, not just the final screen.\n\n### 4. Interactive Prototype & Metrics\nEmbed an interactive Figma prototype. If it went live, what was the outcome? Did task completion time drop? Did error rates decline?\n\nKeep your portfolio to **2-3 deeply structured case studies** rather than 10 shallow redesigns.",
        "created_at": "2026-10-16T14:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q22",
      "user_id": "u22",
      "student_name": "Dhruv Singh",
      "student_username": "dhruv.singh22",
      "student_avatar": "/demo/avatar-5.svg",
      "student_college": "Netaji Subhas University of Technology",
      "student_course": "B.Tech IT",
      "title": "How should students prepare for product management roles?",
      "description": "APM (Associate Product Manager) programs at top companies are fiercely competitive. Since colleges do not have a PM degree, what books, side projects, teardowns, and interview frameworks should an aspiring PM study?",
      "category": "Design and Product Management",
      "tags": [
        "ProductManagement",
        "APMPrep",
        "ProductTeardowns",
        "Frameworks"
      ],
      "college_context": "NSUT",
      "course_context": "3rd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_6",
        "alm_20"
      ],
      "created_at": "2026-10-15T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_22_1",
        "question_id": "q22",
        "author_id": "alm_6",
        "author_name": "Sneha Roy",
        "author_username": "sneha.roy.alumni",
        "author_avatar": "/demo/avatar-5.svg",
        "author_title": "Lead Product Manager",
        "author_company": "Prism Consumer Apps (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 28,
        "content": "Breaking into Product Management as a fresher is challenging because there is no standard college degree for it. But here is the exact playbook to land APM (Associate Product Manager) offers:\n\n### 1. The Core Reading List:\n- *Inspired* by Marty Cagan (the bible of modern product discovery).\n- *Cracking the PM Interview* by Gayle Laakmann McDowell.\n- *The Design of Everyday Things* by Don Norman.\n\n### 2. Build a \"Product Teardown\" Portfolio\nPick a consumer app you use every day (e.g. Spotify, Uber, or Campnect). Write a 3-page teardown document:\n- **User Segment & Goal:** Who is using this feature?\n- **Pain Point:** What friction exists in the user journey?\n- **Proposed Solution:** Wireframe a feature or UX refinement.\n- **North Star Metric:** How would you measure success? (e.g. 7-day retention vs checkout conversion).\n- **Go-To-Market & Risks:** What could go wrong?\n\nPublish these teardowns on Medium, Substack, or LinkedIn. This immediately proves you think like a product owner.\n\n### 3. Master Basic SQL & Data Intuition\nPMs live in data. You must be able to write basic SQL queries (joins, aggregations, cohort analysis) and understand conversion funnels.",
        "created_at": "2026-10-16T14:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q23",
      "user_id": "u23",
      "student_name": "Tara Joshi",
      "student_username": "tara.joshi23",
      "student_avatar": "/demo/avatar-6.svg",
      "student_college": "VIT Vellore",
      "student_course": "B.Tech Computer Science",
      "title": "What practical skills help students enter data analytics?",
      "description": "I know Python syntax and took a statistics class, but entry-level job descriptions ask for SQL, Tableau, data warehousing, and business KPIs. How should a student bridge the gap between academic stats and practical data analysis?",
      "category": "Software Engineering and Technology",
      "tags": [
        "DataAnalytics",
        "SQL",
        "Tableau",
        "BusinessIntelligence"
      ],
      "college_context": "VIT Vellore",
      "course_context": "3rd Year",
      "status": "answered",
      "answers_count": 1,
      "tagged_alumni_ids": [
        "alm_11"
      ],
      "created_at": "2026-10-16T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_23_1",
        "question_id": "q23",
        "author_id": "alm_11",
        "author_name": "Tanvi Saxena",
        "author_username": "tanvi.saxena.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "Senior Business & Data Analytics Consultant",
        "author_company": "Hyperion Global Advisory (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 27,
        "content": "The difference between academic statistics and industry data analytics comes down to **actionable business storytelling**.\n\nHere are the specific tools and skills that hiring managers look for:\n\n### 1. SQL Is 70% of the Job\nDo not just stop at `SELECT * FROM table WHERE id = 1`.\nMaster:\n- Window functions: `ROW_NUMBER()`, `DENSE_RANK()`, `LAG()`, `LEAD()`.\n- Common Table Expressions (`WITH` clauses) for clean, readable multi-stage queries.\n- Joins and NULL handling (e.g. `COALESCE`).\n- Aggregations with `GROUP BY` and `HAVING`.\n\n### 2. Business Intelligence (Tableau / Power BI)\nBuild 2 public dashboards using Kaggle datasets (e.g. e-commerce order fulfillment or flight delay telemetry). \nA great dashboard answers business questions at a glance:\n- What is our top-performing product category this quarter?\n- Where are we losing money?\n\n### 3. The \"So What?\" Rule\nWhen you present a chart, never just describe it: *\"This line went up by 12%.\"*\nAlways state the business consequence: *\"Conversion increased by 12% on the mobile web flow after we removed the mandatory phone verification step, which equates to an estimated $14,000 monthly incremental revenue.\"*",
        "created_at": "2026-10-16T14:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q24",
      "user_id": "u24",
      "student_name": "Yash Mehta",
      "student_username": "yash.mehta24",
      "student_avatar": "/demo/avatar-7.svg",
      "student_college": "Delhi Technological University",
      "student_course": "B.Tech Electrical Engineering",
      "title": "How can students handle rejection during placements?",
      "description": "I got rejected in the final interview rounds of two dream companies this week while seeing friends post their offer letters on LinkedIn. It is emotionally crushing and making me doubt my abilities. How did you cope with placement rejections?",
      "category": "Campus Life and Career Preparation",
      "tags": [
        "PlacementStress",
        "Rejection",
        "MentalHealth",
        "Resilience"
      ],
      "college_context": "DTU",
      "course_context": "4th Year",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_3",
        "alm_23"
      ],
      "created_at": "2026-10-16T14:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_24_1",
        "question_id": "q24",
        "author_id": "alm_23",
        "author_name": "Meenal Agarwal",
        "author_username": "meenal.a.alumni",
        "author_avatar": "/demo/avatar-6.svg",
        "author_title": "People & Culture Business Partner",
        "author_company": "Novus Scale Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 39,
        "content": "I want to validate what you are feeling: **placement season is psychologically one of the most brutal environments you will ever navigate.**\n\nSeeing friends celebrate while you receive rejection emails triggers intense imposter syndrome. Here is how to protect your mental health and bounce back stronger:\n\n### 1. Rejection Is a Data Point, Not a Life Verdict\nAn interview is a 45-minute snapshot evaluated by a human who might have been tired, distracted, or looking for a very specific niche skill. It is NOT an accurate measure of your overall intellect, character, or long-term career ceiling.\n\n### 2. The 24-Hour Rule\nGive yourself 24 hours to grieve, be upset, eat your favorite food, and talk to your parents or close friends. Do NOT open LinkedIn or placement groups during these 24 hours.\n\n### 3. The Post-Interview Debrief\nOnce the emotion subsides, open a notepad and write down:\n- What questions did I answer smoothly?\n- Where did I stumble or lose my train of thought?\n- What was the specific technical concept I need to revise tonight?\n\nTurn the emotional sting into 3 concrete technical action items. The students who succeed are not the ones who never fail—they are the ones who iterate rapidly after each setback.",
        "created_at": "2026-10-16T14:03:00.313Z"
      },
      {
        "id": "ans_24_2",
        "question_id": "q24",
        "author_id": "alm_3",
        "author_name": "Rohan Deshmukh",
        "author_username": "rohan.deshmukh.alumni",
        "author_avatar": "/demo/avatar-2.svg",
        "author_title": "VP of Talent Acquisition & Campus Recruiting",
        "author_company": "Cobalt Talent Partners (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 26,
        "content": "From the recruiter side: In campus drives, we often have 500 applicants and only 10 offers. There are routinely 30-40 candidates who are completely qualified, but we have strict budget caps. Some of the most extraordinary staff engineers and founders I know today were rejected by 5 or 6 companies during campus placements. Stay resilient—your career is a 35-year marathon, not a 3-week sprint.",
        "created_at": "2026-10-16T18:03:00.313Z"
      }
    ]
  },
  {
    "question": {
      "id": "q25",
      "user_id": "u25",
      "student_name": "Ishita Patel",
      "student_username": "ishita.patel25",
      "student_avatar": "/demo/avatar-0.svg",
      "student_college": "IIT Delhi",
      "student_course": "B.Tech Computer Science",
      "title": "What should students focus on during their first 90 days at work?",
      "description": "I am starting my first full-time software engineering job in two months. What habits, communication practices, and technical behaviors distinguish top-performing new grads from those who struggle during probation?",
      "category": "Campus Life and Career Preparation",
      "tags": [
        "FirstJob",
        "First90Days",
        "WorkplaceEtiquette",
        "EarlyCareerSuccess"
      ],
      "college_context": "IIT Delhi",
      "course_context": "Graduating Senior",
      "status": "answered",
      "answers_count": 2,
      "tagged_alumni_ids": [
        "alm_1",
        "alm_17"
      ],
      "created_at": "2026-10-17T02:03:00.313Z"
    },
    "answers": [
      {
        "id": "ans_25_1",
        "question_id": "q25",
        "author_id": "alm_1",
        "author_name": "Aarav Singhal",
        "author_username": "aarav.singhal.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Senior Staff Software Engineer",
        "author_company": "Northstar Technologies (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 36,
        "content": "Congratulations on landing the offer! Starting your first corporate engineering role is exciting, but the skillset shifts from \"passing exams\" to \"delivering collaborative value.\"\n\nHere is your **First 90 Days Survival and Excellence Guide**:\n\n### Days 1 to 30: Onboarding & The 30-Minute Debugging Rule\n- **Setup your environment:** Document every single command or broken step you encounter in the onboarding guide. Submit a pull request fixing the onboarding documentation! Your manager and future hires will love you for this.\n- **The 30-Minute Rule:** When stuck on an error, try debugging it yourself for 30 minutes. If still stuck, ask a senior engineer, but frame it like this:\n  *\"I am trying to run the test suite for module X. I ran into error Y. I checked our internal docs and verified my local environment variables. Could you point me in the right direction?\"*\n  This shows you tried before asking and respects their focus time.\n\n### Days 31 to 60: Shipping Small, Frequent PRs\n- Don't try to rewrite the whole architecture. Pick up small bug fixes, minor UI tweaks, or test coverage gaps.\n- Learn how code reviews work on your team. Accept review comments graciously; code reviews are critique of the code, not critique of you.\n\n### Days 61 to 90: Building Autonomy & Context\n- Understand the business: Why is this feature being built? Who are our customers?\n- Proactively share updates in daily standups without waiting for your manager to ask.\n- Deliver on your commitments. If you promise a task by Wednesday, deliver it on Wednesday or flag blockers early on Tuesday afternoon.",
        "created_at": "2026-10-17T04:03:00.313Z"
      },
      {
        "id": "ans_25_2",
        "question_id": "q25",
        "author_id": "alm_17",
        "author_name": "Pooja Hegde",
        "author_username": "pooja.hegde.alumni",
        "author_avatar": "/demo/avatar-0.svg",
        "author_title": "Early Career Program Manager & Tech Mentor",
        "author_company": "Catalyst Campus Works (Simulated)",
        "is_verified_alumni": 1,
        "helpful_count": 22,
        "content": "A golden soft skill tip: **Schedule a bi-weekly 1-on-1 check-in with your direct manager.** Ask one simple question: *\"What is one thing I am doing well, and what is one area where I can improve over the next two weeks?\"* That proactive hunger for feedback instantly places you in the top 5% of new graduates.",
        "created_at": "2026-10-17T08:03:00.313Z"
      }
    ]
  }
]
